/* ─── /docs content ──────────────────────────────────────────
   Plain-data source for the documentation site, in 3 languages
   (es/en/pt). Each category groups articles by what the reader is
   trying to DO (Divio/task model), not by internal panel names.
   Article bodies are arrays of simple content blocks so DocsPage.js
   can render them without a markdown dependency:
     {t:'p',  c:'...'}                              paragraph
     {t:'h',  c:'...'}                               subheading (anchors the right-rail TOC)
     {t:'list', items:['...']}                       bullet list
     {t:'steps', items:['...']}                      numbered steps
     {t:'table', headers:[...], rows:[[...], ...]}    table
     {t:'callout', variant:'info'|'pro'|'warn', c:'...'}  highlighted note
   `plan` on an article ('pro' | 'enterprise') renders a badge next
   to its title; omit it for features available on every plan.
   Slugs, categories, popular flags and block shapes are identical
   across the 3 languages — only text differs — so a reader switching
   language mid-article lands on the same place. */

const CATEGORIES_ES = [
  { key: 'empezando', title: 'Empezando', description: 'Compra, diseña y activa tu tarjeta, y crea tu cuenta.', icon: 'play' },
  { key: 'perfil', title: 'Tu Perfil Digital', description: 'Plantillas, campos, apariencia y todo lo que puedes personalizar.', icon: 'id' },
  { key: 'guias', title: 'Guías y Buenas Prácticas', description: 'Cómo sacarle el máximo provecho a tu tarjeta y tu perfil.', icon: 'lightbulb' },
  { key: 'leads', title: 'Leads y Red de Contactos', description: 'Leads, Hub, Follow-ups automáticos y Campañas.', icon: 'users' },
  { key: 'analitica', title: 'Analíticas', description: 'Métricas, Insights con IA y reportes exportables.', icon: 'chart' },
  { key: 'problemas', title: 'Solución de problemas', description: 'Qué hacer cuando algo no funciona como esperabas.', icon: 'wrench' },
  { key: 'cuenta', title: 'Cuenta, Planes y Equipos', description: 'Planes, suscripción, configuración y equipos.', icon: 'settings' },
  { key: 'developers', title: 'Desarrolladores e Integraciones', description: 'API pública, API Keys, Webhooks e integraciones.', icon: 'code' },
];

const CATEGORIES_EN = [
  { key: 'empezando', title: 'Getting Started', description: 'Buy, design and activate your card, and create your account.', icon: 'play' },
  { key: 'perfil', title: 'Your Digital Profile', description: 'Templates, fields, appearance and everything you can customize.', icon: 'id' },
  { key: 'guias', title: 'Guides & Best Practices', description: 'How to get the most out of your card and your profile.', icon: 'lightbulb' },
  { key: 'leads', title: 'Leads & Contact Network', description: 'Leads, Hub, automatic Follow-ups and Campaigns.', icon: 'users' },
  { key: 'analitica', title: 'Analytics', description: 'Metrics, AI Insights and exportable reports.', icon: 'chart' },
  { key: 'problemas', title: 'Troubleshooting', description: 'What to do when something isn’t working as expected.', icon: 'wrench' },
  { key: 'cuenta', title: 'Account, Plans & Teams', description: 'Plans, subscription, settings and teams.', icon: 'settings' },
  { key: 'developers', title: 'Developers & Integrations', description: 'Public API, API Keys, Webhooks and integrations.', icon: 'code' },
];

const CATEGORIES_PT = [
  { key: 'empezando', title: 'Primeiros Passos', description: 'Compre, desenhe e ative seu cartão, e crie sua conta.', icon: 'play' },
  { key: 'perfil', title: 'Seu Perfil Digital', description: 'Modelos, campos, aparência e tudo o que você pode personalizar.', icon: 'id' },
  { key: 'guias', title: 'Guias e Boas Práticas', description: 'Como aproveitar ao máximo seu cartão e seu perfil.', icon: 'lightbulb' },
  { key: 'leads', title: 'Leads e Rede de Contatos', description: 'Leads, Hub, Follow-ups automáticos e Campanhas.', icon: 'users' },
  { key: 'analitica', title: 'Analytics', description: 'Métricas, Insights com IA e relatórios exportáveis.', icon: 'chart' },
  { key: 'problemas', title: 'Solução de problemas', description: 'O que fazer quando algo não funciona como esperado.', icon: 'wrench' },
  { key: 'cuenta', title: 'Conta, Planos e Equipes', description: 'Planos, assinatura, configuração e equipes.', icon: 'settings' },
  { key: 'developers', title: 'Desenvolvedores e Integrações', description: 'API pública, API Keys, Webhooks e integrações.', icon: 'code' },
];

export const DOCS_CATEGORIES_I18N = { es: CATEGORIES_ES, en: CATEGORIES_EN, pt: CATEGORIES_PT };

// ═══════════════════════════ EMPEZANDO / GETTING STARTED / PRIMEIROS PASSOS ═══════════════════════════

const ARTICLES_EMPEZANDO_ES = [
  {
    slug: 'que-es-contact',
    category: 'empezando',
    title: '¿Qué es Contact?',
    excerpt: 'La tarjeta NFC que reemplaza tu tarjeta de presentación de papel.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Contact es una tarjeta física con un chip NFC (y un código QR como respaldo) que reemplaza la tarjeta de presentación de papel. Al acercarla al celular de otra persona (o al escanear el QR si su celular no tiene NFC activado), se abre automáticamente tu perfil digital: tus datos de contacto, tu cargo y empresa, tus redes sociales y los enlaces que tú elijas, listos para guardar en la agenda de esa persona con un toque.' },
      { t: 'p', c: 'A diferencia de una tarjeta de papel, tu perfil digital vive en tu cuenta de Contact, no en la tarjeta. Eso significa que puedes cambiar tu cargo, tu foto, tus redes o tu empresa en cualquier momento desde el panel, y el cambio se refleja al instante para cualquiera que vuelva a tocar o escanear tu tarjeta: sin reimprimir nada.' },
      { t: 'h', c: 'Las dos partes del producto' },
      { t: 'list', items: [
        'La tarjeta física: el objeto NFC que compras, diseñas a tu gusto y repartes como tarjeta de presentación.',
        'El perfil digital: la página web personal detrás de la tarjeta, que administras desde tu cuenta de Contact.',
      ] },
      { t: 'callout', variant: 'info', c: 'No necesitas ninguna app para que alguien reciba tu tarjeta: NFC y QR funcionan directo desde el navegador del celular de la otra persona, en iPhone y Android.' },
      { t: 'h', c: 'Por qué una tarjeta NFC en vez de papel' },
      { t: 'p', c: 'Una tarjeta de papel es estática: el día que cambias de cargo, de empresa o de número, hay que reimprimir todo el lote y las que ya repartiste quedan desactualizadas para siempre. Con Contact, la tarjeta física es solo la puerta de entrada: el contenido vive en tu cuenta, así que un solo cambio se refleja instantáneamente en todas las personas que alguna vez tocaron tu tarjeta, sin importar cuántas lleves repartidas.' },
      { t: 'p', c: 'Además, cada interacción con tu tarjeta queda registrada (ver Analíticas), algo que una tarjeta de papel nunca te pudo dar: sabes cuánta gente la tocó, qué red social le interesó más, y quién te dejó sus datos a cambio.' },
    ],
  },
  {
    slug: 'comprar-tarjeta',
    category: 'empezando',
    title: 'Cómo comprar tu tarjeta',
    excerpt: 'Cantidades, envío y pago con MercadoPago.',
    blocks: [
      { t: 'p', c: 'La compra se hace desde /comprar. Puedes elegir una cantidad preestablecida o escribir una cantidad personalizada; el precio por unidad baja a medida que compras más, según la tabla de precios por rango vigente.' },
      { t: 'h', c: 'Envío' },
      { t: 'table', headers: ['Cantidad', 'Envío'], rows: [
        ['2 unidades o más', 'Gratis a todo Chile'],
        ['1 unidad, Región Metropolitana', '$3.500 CLP'],
        ['1 unidad, otras regiones', 'Despacho por Starken, costo según destino'],
      ] },
      { t: 'p', c: 'Si tienes un código de descuento, puedes validarlo antes de pagar y el total se actualiza al instante. El pago se procesa con MercadoPago; una vez aprobado, recibes un número de pedido con el formato ORD-AAAAMMDD-XXXX que vas a necesitar en el siguiente paso para diseñar tu tarjeta.' },
    ],
  },
  {
    slug: 'disenar-tarjeta',
    category: 'empezando',
    title: 'Diseña tu tarjeta física',
    excerpt: 'El Diseñador de Tarjetas: plantillas por rubro y exportación a PDF.',
    blocks: [
      { t: 'p', c: 'Con tu número de pedido a mano, entra al Diseñador de Tarjeta e ingrésalo para validar tu compra. Ahí eliges cómo se va a ver el frente y el dorso de tu tarjeta física.' },
      { t: 'h', c: 'Plantillas disponibles' },
      { t: 'p', c: 'Hay 12 plantillas organizadas por rubro, para que el punto de partida ya tenga sentido visual con tu industria:' },
      { t: 'list', items: ['Corporativo', 'Tecnología', 'Emprendimiento', 'Finanzas', 'Gastronomía', 'Industrial'] },
      { t: 'p', c: 'Cada plantilla trae colores de marca predefinidos que puedes ajustar. Cuando el diseño te convence, lo envías y queda guardado para producción: nuestro equipo recibe el archivo y arma la tarjeta física con exactamente ese diseño.' },
      { t: 'callout', variant: 'info', c: 'El diseño de la tarjeta física (cómo se ve el objeto en tu mano) es independiente del perfil digital (lo que ve la otra persona al escanearla). Puedes cambiar tu perfil digital todas las veces que quieras sin tocar el diseño de la tarjeta.' },
    ],
  },
  {
    slug: 'crear-cuenta',
    category: 'empezando',
    title: 'Crea tu cuenta',
    excerpt: 'El registro en 5 pasos, de tu nombre a tu primer perfil armado.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Crear una cuenta en Contact es un único flujo de 5 pasos que termina con tu primer perfil digital ya armado: no es "crear la cuenta" y después "armar el perfil" por separado, es lo mismo.' },
      { t: 'steps', items: [
        'Tu nombre completo.',
        'Tu cargo y tu empresa.',
        'Tu email y teléfono de contacto, y los enlaces que quieras mostrar (LinkedIn, sitio web, Instagram, WhatsApp, email, llamar, y hasta 4 enlaces personalizados adicionales: Calendly, TikTok, YouTube, GitHub, portafolio, y más).',
        'Una foto de perfil (opcional, puedes agregarla después).',
        'El email y la contraseña con los que vas a iniciar sesión.',
      ] },
      { t: 'p', c: 'Al terminar el paso 5, tu cuenta y tu primer perfil quedan creados y te llevamos directo al panel. Ahí vas a ver una pantalla para elegir tu plan (puedes partir en Free y subir de plan cuando quieras).' },
    ],
  },
  {
    slug: 'primeros-pasos',
    category: 'empezando',
    title: 'Primeros pasos en el panel',
    excerpt: 'Qué revisar apenas entras por primera vez.',
    blocks: [
      { t: 'p', c: 'Con la cuenta creada, esto es lo que te recomendamos revisar primero:' },
      { t: 'list', items: [
        'Revisa tu perfil: todo lo que llenaste en el registro ya está ahí, pero vale la pena repasarlo con calma, especialmente tus enlaces y tu foto.',
        'Elige una plantilla visual para tu perfil en Apariencia (Clásica, Corporativa o Retrato).',
        'Si compraste tarjetas físicas, confirma que el diseño que hiciste en el Diseñador de Tarjeta sea el que quieres producir.',
        'Mira tu plan actual en Configuración → Plan, y compara qué te desbloquearía subir de plan si te interesan Analíticas avanzadas, Leads ilimitados o Equipos.',
      ] },
    ],
  },
  {
    slug: 'glosario',
    category: 'empezando',
    title: 'Glosario',
    excerpt: 'Los términos de Contact explicados en una línea.',
    blocks: [
      { t: 'table', headers: ['Término', 'Qué significa'], rows: [
        ['NFC', 'La tecnología del chip de tu tarjeta: permite que tu perfil se abra con solo acercarla a un celular.'],
        ['Perfil digital', 'La página con tu información detrás de tu tarjeta física, editable desde tu cuenta.'],
        ['Lead', 'Alguien que visitó tu perfil y te dejó sus propios datos a través del formulario "Identificarme".'],
        ['Hub', 'Tu CRM dentro de Contact: la libreta donde reúnes todos tus contactos, vengan de un escaneo o agregados a mano.'],
        ['Follow-up', 'Cada uno de los 3 correos automáticos que se envían después de un escaneo real de tu tarjeta.'],
        ['Campaña', 'Un contenedor con fecha y perfiles vinculados, usado para medir cuántos Leads generó un evento puntual.'],
        ['Webhook', 'Un aviso automático que Contact le manda a un sistema tuyo cada vez que ocurre un evento (hoy, un Lead nuevo).'],
        ['API Key', 'La credencial que generas para que tu propio sistema pueda leer tus datos a través de la API pública.'],
      ] },
    ],
  },
];

const ARTICLES_EMPEZANDO_EN = [
  {
    slug: 'que-es-contact',
    category: 'empezando',
    title: 'What is Contact?',
    excerpt: 'The NFC card that replaces your paper business card.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Contact is a physical card with an NFC chip (and a backup QR code) that replaces the paper business card. When you tap it against someone else’s phone (or they scan the QR code if their phone doesn’t have NFC enabled), your digital profile opens automatically: your contact details, your title and company, your social networks and whichever links you choose, ready to save to that person’s contacts with one tap.' },
      { t: 'p', c: 'Unlike a paper card, your digital profile lives in your Contact account, not on the card itself. That means you can change your title, your photo, your social links or your company at any time from the dashboard, and the change shows up instantly for anyone who taps or scans your card again: nothing to reprint.' },
      { t: 'h', c: 'The two parts of the product' },
      { t: 'list', items: [
        'The physical card: the NFC object you buy, design the way you want, and hand out as your business card.',
        'The digital profile: the personal web page behind the card, which you manage from your Contact account.',
      ] },
      { t: 'callout', variant: 'info', c: 'Nobody needs an app to receive your card: NFC and QR both work straight from the other person’s phone browser, on iPhone and Android.' },
      { t: 'h', c: 'Why an NFC card instead of paper' },
      { t: 'p', c: 'A paper card is static: the day your title, company or number changes, you have to reprint the whole batch, and the ones you already handed out stay outdated forever. With Contact, the physical card is just the entry point: the content lives in your account, so a single edit updates instantly for everyone who has ever tapped your card, no matter how many you’ve handed out.' },
      { t: 'p', c: 'On top of that, every interaction with your card gets logged (see Analytics), something a paper card could never give you: you know how many people tapped it, which social network got the most interest, and who left their own details in return.' },
    ],
  },
  {
    slug: 'comprar-tarjeta',
    category: 'empezando',
    title: 'How to buy your card',
    excerpt: 'Quantities, shipping and payment with MercadoPago.',
    blocks: [
      { t: 'p', c: 'You buy from /comprar. You can pick a preset quantity or type a custom amount; the per-unit price drops the more you buy, following the current volume pricing table.' },
      { t: 'h', c: 'Shipping' },
      { t: 'table', headers: ['Quantity', 'Shipping'], rows: [
        ['2 units or more', 'Free anywhere in Chile'],
        ['1 unit, Metropolitan Region', '$3,500 CLP'],
        ['1 unit, other regions', 'Shipped via Starken, cost depends on destination'],
      ] },
      { t: 'p', c: 'If you have a discount code, you can validate it before paying and the total updates instantly. Payment is processed through MercadoPago; once approved, you get an order number in the format ORD-YYYYMMDD-XXXX, which you’ll need in the next step to design your card.' },
    ],
  },
  {
    slug: 'disenar-tarjeta',
    category: 'empezando',
    title: 'Design your physical card',
    excerpt: 'The Card Designer: templates by industry and PDF export.',
    blocks: [
      { t: 'p', c: 'With your order number in hand, open the Card Designer and enter it to validate your purchase. From there you choose how the front and back of your physical card will look.' },
      { t: 'h', c: 'Available templates' },
      { t: 'p', c: 'There are 12 templates organized by industry, so your starting point already makes visual sense for your field:' },
      { t: 'list', items: ['Corporate', 'Technology', 'Entrepreneurship', 'Finance', 'Food & Beverage', 'Industrial'] },
      { t: 'p', c: 'Each template comes with preset brand colors you can adjust. Once you’re happy with the design, you submit it and it’s saved for production: our team receives the file and builds your physical card with exactly that design.' },
      { t: 'callout', variant: 'info', c: 'The design of the physical card (how the object looks in your hand) is independent from the digital profile (what the other person sees when they scan it). You can change your digital profile as many times as you want without touching the card design.' },
    ],
  },
  {
    slug: 'crear-cuenta',
    category: 'empezando',
    title: 'Create your account',
    excerpt: 'The 5-step signup, from your name to your first profile, already built.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Creating a Contact account is a single 5-step flow that ends with your first digital profile already built: it’s not "create the account" and then "build the profile" separately — it’s the same thing.' },
      { t: 'steps', items: [
        'Your full name.',
        'Your title and your company.',
        'Your contact email and phone, and whichever links you want to show (LinkedIn, website, Instagram, WhatsApp, email, call, and up to 4 additional custom links: Calendly, TikTok, YouTube, GitHub, portfolio, and more).',
        'A profile photo (optional, you can add it later).',
        'The email and password you’ll use to sign in.',
      ] },
      { t: 'p', c: 'Once step 5 is done, your account and your first profile are both created, and you’re taken straight to the dashboard. There you’ll see a screen to pick your plan (you can start on Free and upgrade whenever you want).' },
    ],
  },
  {
    slug: 'primeros-pasos',
    category: 'empezando',
    title: 'First steps in the dashboard',
    excerpt: 'What to check the moment you first log in.',
    blocks: [
      { t: 'p', c: 'With your account created, here’s what we recommend checking first:' },
      { t: 'list', items: [
        'Review your profile: everything you filled in during signup is already there, but it’s worth a careful look, especially your links and your photo.',
        'Pick a visual template for your profile in Appearance (Classic, Corporate or Portrait).',
        'If you bought physical cards, confirm that the design you made in the Card Designer is the one you want produced.',
        'Check your current plan in Settings → Plan, and compare what upgrading would unlock if you’re interested in advanced Analytics, unlimited Leads or Teams.',
      ] },
    ],
  },
  {
    slug: 'glosario',
    category: 'empezando',
    title: 'Glossary',
    excerpt: 'Contact’s terms, explained in one line each.',
    blocks: [
      { t: 'table', headers: ['Term', 'What it means'], rows: [
        ['NFC', 'The chip technology in your card: it lets your profile open just by tapping a phone against it.'],
        ['Digital profile', 'The page with your information behind your physical card, editable from your account.'],
        ['Lead', 'Someone who visited your profile and left their own details through the "Identify me" form.'],
        ['Hub', 'Your CRM inside Contact: the address book where you gather all your contacts, whether scanned in or added by hand.'],
        ['Follow-up', 'Each of the 3 automatic emails sent after a real tap or scan of your card.'],
        ['Campaign', 'A container with a date and linked profiles, used to measure how many Leads a specific event generated.'],
        ['Webhook', 'An automatic notification Contact sends to one of your systems whenever an event happens (today, a new Lead).'],
        ['API Key', 'The credential you generate so your own system can read your data through the public API.'],
      ] },
    ],
  },
];

const ARTICLES_EMPEZANDO_PT = [
  {
    slug: 'que-es-contact',
    category: 'empezando',
    title: 'O que é o Contact?',
    excerpt: 'O cartão NFC que substitui seu cartão de visita de papel.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Contact é um cartão físico com um chip NFC (e um código QR como backup) que substitui o cartão de visita de papel. Ao aproximá-lo do celular de outra pessoa (ou ao escanear o QR, se o celular dela não tiver NFC ativado), o seu perfil digital abre automaticamente: seus dados de contato, cargo e empresa, suas redes sociais e os links que você escolher, prontos para salvar na agenda dessa pessoa com um toque.' },
      { t: 'p', c: 'Diferente de um cartão de papel, seu perfil digital vive na sua conta Contact, não no cartão. Isso significa que você pode trocar seu cargo, foto, redes ou empresa a qualquer momento pelo painel, e a mudança aparece instantaneamente para qualquer pessoa que voltar a tocar ou escanear seu cartão: sem reimprimir nada.' },
      { t: 'h', c: 'As duas partes do produto' },
      { t: 'list', items: [
        'O cartão físico: o objeto NFC que você compra, personaliza e distribui como cartão de visita.',
        'O perfil digital: a página pessoal por trás do cartão, que você administra pela sua conta Contact.',
      ] },
      { t: 'callout', variant: 'info', c: 'Ninguém precisa de nenhum app para receber seu cartão: NFC e QR funcionam direto do navegador do celular da outra pessoa, em iPhone e Android.' },
      { t: 'h', c: 'Por que um cartão NFC em vez de papel' },
      { t: 'p', c: 'Um cartão de papel é estático: no dia em que você troca de cargo, de empresa ou de número, é preciso reimprimir todo o lote, e os que você já distribuiu ficam desatualizados para sempre. Com o Contact, o cartão físico é só a porta de entrada: o conteúdo vive na sua conta, entao uma única mudança se reflete instantaneamente para todos que já tocaram seu cartão, independente de quantos você tenha distribuído.' },
      { t: 'p', c: 'Além disso, cada interação com seu cartão fica registrada (veja Analytics), algo que um cartão de papel nunca pôde te dar: você sabe quantas pessoas tocaram nele, qual rede social gerou mais interesse e quem deixou os próprios dados em troca.' },
    ],
  },
  {
    slug: 'comprar-tarjeta',
    category: 'empezando',
    title: 'Como comprar seu cartão',
    excerpt: 'Quantidades, envio e pagamento com MercadoPago.',
    blocks: [
      { t: 'p', c: 'A compra é feita em /comprar. Você pode escolher uma quantidade pré-definida ou digitar uma quantidade personalizada; o preço por unidade cai conforme você compra mais, seguindo a tabela de preços por faixa vigente.' },
      { t: 'h', c: 'Envio' },
      { t: 'table', headers: ['Quantidade', 'Envio'], rows: [
        ['2 unidades ou mais', 'Grátis para todo o Chile'],
        ['1 unidade, Região Metropolitana', '$3.500 CLP'],
        ['1 unidade, outras regiões', 'Envio via Starken, custo conforme o destino'],
      ] },
      { t: 'p', c: 'Se você tiver um código de desconto, pode validá-lo antes de pagar e o total se atualiza na hora. O pagamento é processado pelo MercadoPago; depois de aprovado, você recebe um número de pedido no formato ORD-AAAAMMDD-XXXX, que você vai precisar no próximo passo para desenhar seu cartão.' },
    ],
  },
  {
    slug: 'disenar-tarjeta',
    category: 'empezando',
    title: 'Desenhe seu cartão físico',
    excerpt: 'O Designer de Cartões: modelos por segmento e exportação em PDF.',
    blocks: [
      { t: 'p', c: 'Com seu número de pedido em mãos, entre no Designer de Cartões e informe-o para validar sua compra. Aí você escolhe como vai ficar a frente e o verso do seu cartão físico.' },
      { t: 'h', c: 'Modelos disponíveis' },
      { t: 'p', c: 'Há 12 modelos organizados por segmento, para que o ponto de partida já faça sentido visual com sua indústria:' },
      { t: 'list', items: ['Corporativo', 'Tecnologia', 'Empreendedorismo', 'Finanças', 'Gastronomia', 'Industrial'] },
      { t: 'p', c: 'Cada modelo traz cores de marca predefinidas que você pode ajustar. Quando o design estiver do seu gosto, você o envia e ele fica salvo para produção: nossa equipe recebe o arquivo e monta o cartão físico exatamente com esse design.' },
      { t: 'callout', variant: 'info', c: 'O design do cartão físico (como o objeto fica na sua mão) é independente do perfil digital (o que a outra pessoa vê ao escaneá-lo). Você pode mudar seu perfil digital quantas vezes quiser sem tocar no design do cartão.' },
    ],
  },
  {
    slug: 'crear-cuenta',
    category: 'empezando',
    title: 'Crie sua conta',
    excerpt: 'O cadastro em 5 passos, do seu nome ao seu primeiro perfil já montado.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Criar uma conta no Contact é um único fluxo de 5 passos que termina com seu primeiro perfil digital já montado: não é "criar a conta" e depois "montar o perfil" separadamente, é a mesma coisa.' },
      { t: 'steps', items: [
        'Seu nome completo.',
        'Seu cargo e sua empresa.',
        'Seu email e telefone de contato, e os links que quiser mostrar (LinkedIn, site, Instagram, WhatsApp, email, chamada, e até 4 links personalizados adicionais: Calendly, TikTok, YouTube, GitHub, portfólio, e mais).',
        'Uma foto de perfil (opcional, você pode adicionar depois).',
        'O email e a senha com os quais você vai entrar.',
      ] },
      { t: 'p', c: 'Ao terminar o passo 5, sua conta e seu primeiro perfil já estão criados e você é levado direto ao painel. Lá você vai ver uma tela para escolher seu plano (pode comear no Gratuito e fazer upgrade quando quiser).' },
    ],
  },
  {
    slug: 'primeros-pasos',
    category: 'empezando',
    title: 'Primeiros passos no painel',
    excerpt: 'O que verificar logo que você entra pela primeira vez.',
    blocks: [
      { t: 'p', c: 'Com a conta criada, aqui está o que recomendamos verificar primeiro:' },
      { t: 'list', items: [
        'Revise seu perfil: tudo o que você preencheu no cadastro já está lá, mas vale a pena revisar com calma, especialmente seus links e sua foto.',
        'Escolha um modelo visual para seu perfil em Aparência (Clássico, Corporativo ou Retrato).',
        'Se você comprou cartões físicos, confirme que o design feito no Designer de Cartões é o que você quer produzir.',
        'Veja seu plano atual em Configurações → Plano, e compare o que o upgrade desbloquearia se você tiver interesse em Analytics avançado, Leads ilimitados ou Equipes.',
      ] },
    ],
  },
  {
    slug: 'glosario',
    category: 'empezando',
    title: 'Glossário',
    excerpt: 'Os termos do Contact explicados em uma linha.',
    blocks: [
      { t: 'table', headers: ['Termo', 'O que significa'], rows: [
        ['NFC', 'A tecnologia do chip do seu cartão: permite que seu perfil abra só de aproximá-lo de um celular.'],
        ['Perfil digital', 'A página com suas informações por trás do seu cartão físico, editável pela sua conta.'],
        ['Lead', 'Alguém que visitou seu perfil e deixou os próprios dados pelo formulário "Identificar-me".'],
        ['Hub', 'Seu CRM dentro do Contact: a agenda onde você reúne todos os seus contatos, vindos de um escaneamento ou adicionados manualmente.'],
        ['Follow-up', 'Cada um dos 3 emails automáticos enviados depois de um toque ou escaneamento real do seu cartão.'],
        ['Campanha', 'Um conjunto com data e perfis vinculados, usado para medir quantos Leads um evento específico gerou.'],
        ['Webhook', 'Um aviso automático que o Contact envia a um sistema seu sempre que ocorre um evento (hoje, um novo Lead).'],
        ['API Key', 'A credencial que você gera para que seu próprio sistema possa ler seus dados pela API pública.'],
      ] },
    ],
  },
];

// ═══════════════════════════ PERFIL / PROFILE / PERFIL ═══════════════════════════

const ARTICLES_PERFIL_ES = [
  {
    slug: 'plantillas',
    category: 'perfil',
    title: 'Plantillas: Clásica, Corporativa y Retrato',
    excerpt: 'Las 3 plantillas visuales de tu perfil público.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Tu perfil público tiene tres plantillas para elegir, cada una con una lógica visual distinta. Cambiar de plantilla no te hace perder ningún dato: tu información se reacomoda al nuevo layout automáticamente.' },
      { t: 'table', headers: ['Plantilla', 'Cómo se ve'], rows: [
        ['Clásica', 'Fondo inmersivo con degradado de tu color principal y tu tarjeta de contacto centrada: la opción más versátil y la que usan la mayoría de los perfiles nuevos.'],
        ['Corporativa', 'Un banner a todo el ancho arriba y la información distribuida en columnas, con colores de fondo y de texto que puedes definir de forma independiente: pensada para marcas con una identidad visual más formal.'],
        ['Retrato', 'Tu foto de perfil a página completa arriba, con un panel de color abajo con tus datos: ideal si quieres que la foto sea lo primero que se vea.'],
      ] },
      { t: 'p', c: 'Puedes cambiar de plantilla cuantas veces quieras desde Apariencia, sin costo adicional y sin importar tu plan.' },
    ],
  },
  {
    slug: 'campos-editables',
    category: 'perfil',
    title: 'Todo lo que puedes editar en tu perfil',
    excerpt: 'Referencia completa de campos: datos, redes, enlaces y archivos.',
    blocks: [
      { t: 'h', c: 'Datos personales' },
      { t: 'list', items: ['Nombre y apellido', 'Cargo', 'Empresa', 'Biografía corta', 'Email y teléfono de contacto'] },
      { t: 'h', c: 'Redes y enlaces' },
      { t: 'list', items: ['WhatsApp, Instagram, TikTok, LinkedIn, Facebook y sitio web', 'Hasta 4 enlaces personalizados, cada uno con su propio título y URL (Calendly, portafolio, YouTube, Notion, lo que necesites)'] },
      { t: 'h', c: 'Imágenes' },
      { t: 'list', items: ['Foto de perfil', 'Imagen de fondo', 'Logo de tu empresa'] },
      { t: 'callout', variant: 'pro', c: 'La imagen de fondo personalizada está disponible desde el plan Pro. En Free, tu perfil usa el fondo por defecto de tu plantilla.' },
      { t: 'h', c: 'Archivos descargables' },
      { t: 'p', c: 'Puedes subir hasta 2 archivos (PDF, Word, Excel, PowerPoint o ZIP, máximo 3 MB cada uno) que aparecen como botones de descarga directa en tu perfil público: útil para una presentación, un catálogo o un brochure.' },
    ],
  },
  {
    slug: 'apariencia',
    category: 'perfil',
    title: 'Personaliza la apariencia',
    excerpt: 'Colores de tema y contraste automático.',
    blocks: [
      { t: 'p', c: 'En Apariencia eliges tu plantilla y el color principal de tu perfil. Puedes usar uno de los temas predefinidos o elegir un color propio: el sistema genera automáticamente una paleta completa a partir de ese color, cuidando que el contraste entre texto y fondo siga siendo legible.' },
      { t: 'steps', items: [
        'Entra a Apariencia desde el panel.',
        'Elige tu plantilla (Clásica, Corporativa o Retrato).',
        'Elige un tema predefinido o un color personalizado.',
        'Guarda los cambios: se aplican a tu perfil público de inmediato.',
      ] },
      { t: 'callout', variant: 'pro', c: 'Quitar la marca de agua "Contact" de tu perfil público es una función de los planes pagos.' },
    ],
  },
  {
    slug: 'archivos-adjuntos',
    category: 'perfil',
    title: 'Agrega archivos descargables a tu perfil',
    excerpt: 'Cómo y para qué usar los 2 slots de archivos.',
    blocks: [
      { t: 'p', c: 'Los archivos adjuntos son uno de los lugares donde más vale la pena invertir tiempo: es tu oportunidad de dejar algo tangible (un PDF, una propuesta, un catálogo) en manos de quien tocó tu tarjeta, sin que tenga que pedírtelo por otro canal.' },
      { t: 'list', items: [
        'Formatos admitidos: PDF, Word, Excel, PowerPoint y ZIP.',
        'Tamaño máximo: 3 MB por archivo (si tu archivo pesa más, comprímelo antes de subirlo).',
        'Tienes 2 espacios disponibles; puedes reemplazar un archivo existente en cualquier momento.',
      ] },
    ],
  },
  {
    slug: 'multiples-perfiles',
    category: 'perfil',
    title: 'Múltiples perfiles en una cuenta',
    excerpt: 'Cuántos perfiles puedes tener según tu plan.',
    blocks: [
      { t: 'p', c: 'Una misma cuenta puede administrar más de un perfil digital: útil si manejas distintos roles o marcas personales. Desde el administrador de perfiles puedes crear uno nuevo, cambiar cuál es tu perfil activo, marcar uno como predeterminado, o eliminarlo.' },
      { t: 'table', headers: ['Plan', 'Perfiles incluidos'], rows: [
        ['Free', '1'],
        ['Pro', '3'],
        ['Enterprise', '10 (pensado para equipos: ver la sección de Equipos)'],
      ] },
    ],
  },
  {
    slug: 'apple-wallet',
    category: 'perfil',
    title: 'Apple Wallet: agrega tu tarjeta al celular',
    excerpt: 'Para quien visita tu perfil, en un toque.',
    blocks: [
      { t: 'p', c: 'Cualquiera que visite tu perfil público puede agregarlo a su Apple Wallet con un botón, sin necesidad de guardarte como contacto por separado. El pase incluye tu nombre, tu cargo, tu empresa y un código QR directo a tu perfil, siempre listo en el celular de esa persona.' },
      { t: 'callout', variant: 'info', c: 'Esta función está disponible para quien recibe tu perfil (no es algo que tú configures en tu cuenta) y aparece automáticamente como un botón más en tu perfil público.' },
      { t: 'p', c: 'A diferencia de guardar tu contacto en la agenda (donde solo queda un nombre y un teléfono perdido entre cientos de contactos), un pase en Wallet se queda visible y accesible desde la pantalla de bloqueo o la app Wallet del celular: mucho más difícil de olvidar que un contacto más en la lista.' },
    ],
  },
  {
    slug: 'nfc-vs-qr',
    category: 'perfil',
    title: 'Cómo funciona el NFC y el código QR',
    excerpt: 'La tecnología detrás del "toque" de tu tarjeta.',
    blocks: [
      { t: 'p', c: 'Tu tarjeta física trae un chip NFC (Near Field Communication) grabado con la dirección de tu perfil. Al acercarla a la parte trasera de un celular con NFC activado (la gran mayoría de los iPhone y Android desde hace varios años), el teléfono detecta el chip y abre tu perfil en el navegador, sin necesidad de ninguna app instalada.' },
      { t: 'p', c: 'Si el celular de la otra persona no tiene NFC, o lo tiene desactivado, tu tarjeta también trae impreso un código QR que lleva exactamente al mismo perfil: por eso decimos que NFC y QR son dos caminos al mismo destino, nunca te vas a quedar sin forma de compartir tu tarjeta.' },
      { t: 'h', c: 'Cuándo conviene usar cada uno' },
      { t: 'p', c: 'En la práctica, el NFC es más rápido (un toque, sin apuntar con la cámara) y funciona incluso con la pantalla del celular apagada en muchos modelos. El QR es el respaldo universal: funciona en cualquier celular con cámara, sin depender de si el NFC del otro teléfono está activado, y es la opción natural si estás mostrando tu tarjeta a distancia (por ejemplo, proyectada en una presentación) en vez de acercándola físicamente.' },
    ],
  },
  {
    slug: 'compatibilidad-dispositivos',
    category: 'perfil',
    title: 'Compatibilidad de dispositivos',
    excerpt: 'Qué necesita el celular de quien recibe tu tarjeta.',
    blocks: [
      { t: 'p', c: 'Tu tarjeta está pensada para funcionar sin fricción en la inmensa mayoría de los celulares actuales, pero vale la pena conocer los dos requisitos reales para que el "toque" NFC funcione de inmediato.' },
      { t: 'h', c: 'Para que el NFC funcione' },
      { t: 'list', items: [
        'El celular debe tener NFC (prácticamente todos los iPhone desde el 7 en adelante, y la gran mayoría de los Android de los últimos años, lo traen).',
        'El NFC debe estar activado. En iPhone viene activado por defecto y no se puede desactivar; en Android a veces está apagado en Ajustes y hay que encenderlo una vez.',
      ] },
      { t: 'p', c: 'Ninguno de los dos requisitos depende de ti ni de tu tarjeta: son del celular de la persona que recibe tu perfil. Por eso la tarjeta siempre trae el código QR como respaldo: si el NFC del otro celular no está disponible por cualquier razón, el QR abre exactamente el mismo perfil sin depender de nada de lo anterior.' },
      { t: 'callout', variant: 'info', c: 'Del lado de quien ve tu perfil no se necesita ninguna app: tanto el NFC como el QR abren tu perfil directo en el navegador del celular.' },
    ],
  },
];

const ARTICLES_PERFIL_EN = [
  {
    slug: 'plantillas',
    category: 'perfil',
    title: 'Templates: Classic, Corporate and Portrait',
    excerpt: 'The 3 visual templates for your public profile.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Your public profile has three templates to choose from, each with its own visual logic. Switching templates never loses any data: your information reflows into the new layout automatically.' },
      { t: 'table', headers: ['Template', 'What it looks like'], rows: [
        ['Classic', 'An immersive background with a gradient of your main color and your contact card centered: the most versatile option, and the one most new profiles use.'],
        ['Corporate', 'A full-width banner at the top and information laid out in columns, with background and text colors you can set independently: built for brands with a more formal visual identity.'],
        ['Portrait', 'Your profile photo full-page at the top, with a colored panel below holding your details: ideal if you want the photo to be the first thing people see.'],
      ] },
      { t: 'p', c: 'You can switch templates as many times as you want from Appearance, at no extra cost and regardless of your plan.' },
    ],
  },
  {
    slug: 'campos-editables',
    category: 'perfil',
    title: 'Everything you can edit on your profile',
    excerpt: 'Full field reference: details, networks, links and files.',
    blocks: [
      { t: 'h', c: 'Personal details' },
      { t: 'list', items: ['First and last name', 'Title', 'Company', 'Short bio', 'Contact email and phone'] },
      { t: 'h', c: 'Networks and links' },
      { t: 'list', items: ['WhatsApp, Instagram, TikTok, LinkedIn, Facebook and website', 'Up to 4 custom links, each with its own title and URL (Calendly, portfolio, YouTube, Notion, whatever you need)'] },
      { t: 'h', c: 'Images' },
      { t: 'list', items: ['Profile photo', 'Background image', 'Your company logo'] },
      { t: 'callout', variant: 'pro', c: 'A custom background image is available from the Pro plan up. On Free, your profile uses your template’s default background.' },
      { t: 'h', c: 'Downloadable files' },
      { t: 'p', c: 'You can upload up to 2 files (PDF, Word, Excel, PowerPoint or ZIP, 3 MB max each) that show up as direct-download buttons on your public profile: useful for a presentation, a catalog or a brochure.' },
    ],
  },
  {
    slug: 'apariencia',
    category: 'perfil',
    title: 'Customize the appearance',
    excerpt: 'Theme colors and automatic contrast.',
    blocks: [
      { t: 'p', c: 'In Appearance you choose your template and your profile’s main color. You can use one of the preset themes or pick your own color: the system automatically generates a full palette from that color, making sure the contrast between text and background stays readable.' },
      { t: 'steps', items: [
        'Open Appearance from the dashboard.',
        'Choose your template (Classic, Corporate or Portrait).',
        'Pick a preset theme or a custom color.',
        'Save your changes: they apply to your public profile immediately.',
      ] },
      { t: 'callout', variant: 'pro', c: 'Removing the "Contact" watermark from your public profile is a paid-plan feature.' },
    ],
  },
  {
    slug: 'archivos-adjuntos',
    category: 'perfil',
    title: 'Add downloadable files to your profile',
    excerpt: 'How and why to use the 2 file slots.',
    blocks: [
      { t: 'p', c: 'Attached files are one of the places most worth spending time on: it’s your chance to leave something tangible (a PDF, a proposal, a catalog) in the hands of whoever tapped your card, without them having to ask for it through another channel.' },
      { t: 'list', items: [
        'Supported formats: PDF, Word, Excel, PowerPoint and ZIP.',
        'Max size: 3 MB per file (if your file is heavier, compress it before uploading).',
        'You have 2 available slots; you can replace an existing file at any time.',
      ] },
    ],
  },
  {
    slug: 'multiples-perfiles',
    category: 'perfil',
    title: 'Multiple profiles on one account',
    excerpt: 'How many profiles you can have, based on your plan.',
    blocks: [
      { t: 'p', c: 'A single account can manage more than one digital profile: useful if you handle different roles or personal brands. From the profiles manager you can create a new one, switch which one is your active profile, mark one as default, or delete it.' },
      { t: 'table', headers: ['Plan', 'Profiles included'], rows: [
        ['Free', '1'],
        ['Pro', '3'],
        ['Enterprise', '10 (built for teams: see the Teams section)'],
      ] },
    ],
  },
  {
    slug: 'apple-wallet',
    category: 'perfil',
    title: 'Apple Wallet: add your card to a phone',
    excerpt: 'For whoever visits your profile, in one tap.',
    blocks: [
      { t: 'p', c: 'Anyone who visits your public profile can add it to their Apple Wallet with a button, with no need to save you as a separate contact. The pass includes your name, title, company and a QR code straight to your profile, always ready on that person’s phone.' },
      { t: 'callout', variant: 'info', c: 'This feature is for whoever receives your profile (it’s not something you configure on your account) and shows up automatically as another button on your public profile.' },
      { t: 'p', c: 'Unlike saving your contact to the phone’s address book (where it’s just a name and a number lost among hundreds of contacts), a Wallet pass stays visible and reachable from the lock screen or the Wallet app: much harder to forget than one more entry in a list.' },
    ],
  },
  {
    slug: 'nfc-vs-qr',
    category: 'perfil',
    title: 'How NFC and the QR code work',
    excerpt: 'The technology behind your card’s "tap".',
    blocks: [
      { t: 'p', c: 'Your physical card has an NFC (Near Field Communication) chip encoded with your profile’s address. When you tap it against the back of a phone with NFC enabled (the vast majority of iPhones and Androids for several years now), the phone detects the chip and opens your profile in the browser, with no app required.' },
      { t: 'p', c: 'If the other person’s phone doesn’t have NFC, or has it turned off, your card also has a printed QR code that leads to exactly the same profile: that’s why we say NFC and QR are two paths to the same destination — you’ll never be left without a way to share your card.' },
      { t: 'h', c: 'When to use each one' },
      { t: 'p', c: 'In practice, NFC is faster (one tap, no aiming a camera) and works even with the phone’s screen off on many models. QR is the universal backup: it works on any phone with a camera, regardless of whether the other phone’s NFC is on, and it’s the natural choice if you’re showing your card at a distance (for example, projected in a presentation) instead of tapping it physically.' },
    ],
  },
  {
    slug: 'compatibilidad-dispositivos',
    category: 'perfil',
    title: 'Device compatibility',
    excerpt: 'What the receiving phone needs.',
    blocks: [
      { t: 'p', c: 'Your card is built to work frictionlessly on the vast majority of today’s phones, but it’s worth knowing the two real requirements for the NFC "tap" to work right away.' },
      { t: 'h', c: 'For NFC to work' },
      { t: 'list', items: [
        'The phone must have NFC (practically every iPhone from the 7 onward, and the large majority of Androids from recent years, have it).',
        'NFC must be enabled. On iPhone it’s on by default and can’t be turned off; on Android it’s sometimes off in Settings and needs to be switched on once.',
      ] },
      { t: 'p', c: 'Neither requirement depends on you or your card: they’re about the phone of the person receiving your profile. That’s why the card always has the QR code as a backup: if the other phone’s NFC isn’t available for any reason, the QR opens exactly the same profile without depending on any of the above.' },
      { t: 'callout', variant: 'info', c: 'On the side of whoever is viewing your profile, no app is needed: both NFC and QR open your profile straight in the phone’s browser.' },
    ],
  },
];

const ARTICLES_PERFIL_PT = [
  {
    slug: 'plantillas',
    category: 'perfil',
    title: 'Modelos: Clássico, Corporativo e Retrato',
    excerpt: 'Os 3 modelos visuais do seu perfil público.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Seu perfil público tem três modelos para escolher, cada um com uma lógica visual diferente. Trocar de modelo não faz você perder nenhum dado: suas informações se reorganizam no novo layout automaticamente.' },
      { t: 'table', headers: ['Modelo', 'Como fica'], rows: [
        ['Clássico', 'Fundo imersivo com degradê da sua cor principal e seu cartão de contato centralizado: a opção mais versátil e a que a maioria dos novos perfis usa.'],
        ['Corporativo', 'Um banner em toda a largura no topo e as informações distribuídas em colunas, com cores de fundo e de texto definidas de forma independente: pensado para marcas com identidade visual mais formal.'],
        ['Retrato', 'Sua foto de perfil em página inteira no topo, com um painel de cor abaixo com seus dados: ideal se você quer que a foto seja a primeira coisa vista.'],
      ] },
      { t: 'p', c: 'Você pode trocar de modelo quantas vezes quiser em Aparência, sem custo adicional e independente do seu plano.' },
    ],
  },
  {
    slug: 'campos-editables',
    category: 'perfil',
    title: 'Tudo o que você pode editar no seu perfil',
    excerpt: 'Referência completa de campos: dados, redes, links e arquivos.',
    blocks: [
      { t: 'h', c: 'Dados pessoais' },
      { t: 'list', items: ['Nome e sobrenome', 'Cargo', 'Empresa', 'Biografia curta', 'Email e telefone de contato'] },
      { t: 'h', c: 'Redes e links' },
      { t: 'list', items: ['WhatsApp, Instagram, TikTok, LinkedIn, Facebook e site', 'Até 4 links personalizados, cada um com seu próprio título e URL (Calendly, portfólio, YouTube, Notion, o que você precisar)'] },
      { t: 'h', c: 'Imagens' },
      { t: 'list', items: ['Foto de perfil', 'Imagem de fundo', 'Logo da sua empresa'] },
      { t: 'callout', variant: 'pro', c: 'A imagem de fundo personalizada está disponível a partir do plano Pro. No Gratuito, seu perfil usa o fundo padrão do seu modelo.' },
      { t: 'h', c: 'Arquivos para download' },
      { t: 'p', c: 'Você pode enviar até 2 arquivos (PDF, Word, Excel, PowerPoint ou ZIP, máximo 3 MB cada) que aparecem como botões de download direto no seu perfil público: útil para uma apresentação, um catálogo ou um brochure.' },
    ],
  },
  {
    slug: 'apariencia',
    category: 'perfil',
    title: 'Personalize a aparência',
    excerpt: 'Cores de tema e contraste automático.',
    blocks: [
      { t: 'p', c: 'Em Aparência você escolhe seu modelo e a cor principal do seu perfil. Pode usar um dos temas predefinidos ou escolher uma cor própria: o sistema gera automaticamente uma paleta completa a partir dessa cor, cuidando para que o contraste entre texto e fundo continue legível.' },
      { t: 'steps', items: [
        'Entre em Aparência pelo painel.',
        'Escolha seu modelo (Clássico, Corporativo ou Retrato).',
        'Escolha um tema predefinido ou uma cor personalizada.',
        'Salve as alterações: elas se aplicam ao seu perfil público imediatamente.',
      ] },
      { t: 'callout', variant: 'pro', c: 'Remover a marca d’água "Contact" do seu perfil público é um recurso dos planos pagos.' },
    ],
  },
  {
    slug: 'archivos-adjuntos',
    category: 'perfil',
    title: 'Adicione arquivos para download ao seu perfil',
    excerpt: 'Como e para que usar os 2 espaços de arquivos.',
    blocks: [
      { t: 'p', c: 'Os arquivos anexados são um dos lugares em que mais vale a pena investir tempo: é sua chance de deixar algo tangível (um PDF, uma proposta, um catálogo) nas mãos de quem tocou seu cartão, sem que precise pedir por outro canal.' },
      { t: 'list', items: [
        'Formatos aceitos: PDF, Word, Excel, PowerPoint e ZIP.',
        'Tamanho máximo: 3 MB por arquivo (se o seu arquivo for mais pesado, comprima antes de enviar).',
        'Você tem 2 espaços disponíveis; pode substituir um arquivo existente a qualquer momento.',
      ] },
    ],
  },
  {
    slug: 'multiples-perfiles',
    category: 'perfil',
    title: 'Múltiplos perfis em uma conta',
    excerpt: 'Quantos perfis você pode ter de acordo com seu plano.',
    blocks: [
      { t: 'p', c: 'Uma mesma conta pode administrar mais de um perfil digital: útil se você gerencia papéis ou marcas pessoais diferentes. No gerenciador de perfis você pode criar um novo, trocar qual é seu perfil ativo, marcar um como padrão, ou excluí-lo.' },
      { t: 'table', headers: ['Plano', 'Perfis incluídos'], rows: [
        ['Gratuito', '1'],
        ['Pro', '3'],
        ['Enterprise', '10 (pensado para equipes: veja a seção de Equipes)'],
      ] },
    ],
  },
  {
    slug: 'apple-wallet',
    category: 'perfil',
    title: 'Apple Wallet: adicione seu cartão ao celular',
    excerpt: 'Para quem visita seu perfil, em um toque.',
    blocks: [
      { t: 'p', c: 'Qualquer pessoa que visite seu perfil público pode adicioná-lo ao Apple Wallet com um botão, sem precisar salvar você como contato separadamente. O passe inclui seu nome, cargo, empresa e um código QR direto para seu perfil, sempre disponível no celular dessa pessoa.' },
      { t: 'callout', variant: 'info', c: 'Esse recurso é para quem recebe seu perfil (não é algo que você configura na sua conta) e aparece automaticamente como mais um botão no seu perfil público.' },
      { t: 'p', c: 'Diferente de salvar seu contato na agenda (onde fica só um nome e um telefone perdidos entre centenas de contatos), um passe no Wallet permanece visível e acessível pela tela de bloqueio ou pelo app Wallet do celular: muito mais difícil de esquecer do que mais um contato na lista.' },
    ],
  },
  {
    slug: 'nfc-vs-qr',
    category: 'perfil',
    title: 'Como funcionam o NFC e o código QR',
    excerpt: 'A tecnologia por trás do "toque" do seu cartão.',
    blocks: [
      { t: 'p', c: 'Seu cartão físico traz um chip NFC (Near Field Communication) grava do com o endereço do seu perfil. Ao aproximá-lo da parte de trás de um celular com NFC ativado (a grande maioria dos iPhones e Androids há vários anos), o telefone detecta o chip e abre seu perfil no navegador, sem precisar de nenhum app instalado.' },
      { t: 'p', c: 'Se o celular da outra pessoa não tiver NFC, ou estiver desativado, seu cartão também traz impresso um código QR que leva exatamente ao mesmo perfil: por isso dizemos que NFC e QR são dois caminhos para o mesmo destino, você nunca vai ficar sem como compartilhar seu cartão.' },
      { t: 'h', c: 'Quando usar cada um' },
      { t: 'p', c: 'Na prática, o NFC é mais rápido (um toque, sem apontar a câmera) e funciona mesmo com a tela do celular apagada em muitos modelos. O QR é o backup universal: funciona em qualquer celular com câmera, sem depender do NFC do outro celular estar ativado, e é a opção natural se você está mostrando seu cartão a distância (por exemplo, projetado em uma apresentação) em vez de aproximá-lo fisicamente.' },
    ],
  },
  {
    slug: 'compatibilidad-dispositivos',
    category: 'perfil',
    title: 'Compatibilidade de dispositivos',
    excerpt: 'O que o celular de quem recebe seu cartão precisa ter.',
    blocks: [
      { t: 'p', c: 'Seu cartão é pensado para funcionar sem fricção na grande maioria dos celulares atuais, mas vale a pena conhecer os dois requisitos reais para que o "toque" NFC funcione de imediato.' },
      { t: 'h', c: 'Para o NFC funcionar' },
      { t: 'list', items: [
        'O celular precisa ter NFC (praticamente todos os iPhones a partir do 7, e a grande maioria dos Androids dos últimos anos, têm).',
        'O NFC precisa estar ativado. No iPhone vem ativado por padrão e não pode ser desativado; no Android às vezes vem desligado nas Configurações e precisa ser ligado uma vez.',
      ] },
      { t: 'p', c: 'Nenhum dos dois requisitos depende de você ou do seu cartão: são do celular de quem recebe seu perfil. Por isso o cartão sempre traz o código QR como backup: se o NFC do outro celular não estiver disponível por qualquer motivo, o QR abre exatamente o mesmo perfil sem depender de nada disso.' },
      { t: 'callout', variant: 'info', c: 'Do lado de quem vê seu perfil, nenhum app é necessário: tanto o NFC quanto o QR abrem seu perfil direto no navegador do celular.' },
    ],
  },
];

// ═══════════════════════════ GUÍAS / GUIDES / GUIAS ═══════════════════════════

const ARTICLES_GUIAS_ES = [
  {
    slug: 'perfil-que-convierte',
    category: 'guias',
    title: 'Arma un perfil que convierta',
    excerpt: 'Qué priorizar para que tu tarjeta realmente genere contactos.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Tener una tarjeta NFC no genera resultados por sí solo: lo que convierte un "toque" en un contacto real es lo que la persona encuentra del otro lado. Esto es lo que más impacto tiene, en orden:' },
      { t: 'steps', items: [
        'Una foto real y profesional. Un perfil sin foto (o con una foto genérica) se siente menos confiable que uno con una cara reconocible.',
        'Tu cargo y empresa claros, sin abreviaturas que solo tú entiendes.',
        'Los 2 o 3 canales por los que de verdad quieres que te contacten, no los 6 que tienes disponibles: un perfil saturado de botones diluye la acción que te importa.',
        'Al menos un archivo adjunto si tu rubro lo justifica (una propuesta, un catálogo, un portafolio): es la diferencia entre "guardaron mi contacto" y "se llevaron algo tuyo".',
      ] },
      { t: 'p', c: 'Después de eso, la plantilla y los colores son el último 10%: ayudan a la primera impresión, pero no reemplazan a los puntos anteriores.' },
    ],
  },
  {
    slug: 'hub-vs-leads-vs-campanas',
    category: 'guias',
    title: 'Hub, Leads y Campañas: cuándo usar cada uno',
    excerpt: 'Los tres conceptos que más se confunden en la plataforma.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Son tres cosas relacionadas pero distintas, y la confusión más común es no tener claro cuál es cuál:' },
      { t: 'table', headers: ['Concepto', 'Qué es', 'Cómo llega la gente ahí'], rows: [
        ['Leads', 'Gente que te dejó sus datos a través de tu perfil público.', 'Automático: cualquiera que complete "Identificarme".'],
        ['Hub', 'Tu libreta de contactos general dentro de Contact.', 'Manual, promoviendo un Lead, o escaneando una tarjeta física con la cámara.'],
        ['Campañas', 'Una forma de agrupar y medir los Leads de un evento puntual.', 'Automático para cualquier Lead que entre mientras la campaña está activa.'],
      ] },
      { t: 'p', c: 'En otras palabras: Leads es la bandeja de entrada de gente nueva, Hub es donde vive tu red completa una vez que decidiste quedarte con ese contacto, y Campañas es un lente para medir Leads agrupados por evento: no reemplaza a los otros dos, los organiza.' },
    ],
  },
  {
    slug: 'sacar-provecho-evento',
    category: 'guias',
    title: 'Cómo sacarle provecho a un evento con tu tarjeta',
    excerpt: 'Antes, durante y después de una feria o charla.',
    blocks: [
      { t: 'h', c: 'Antes' },
      { t: 'p', c: 'Crea una Campaña con la fecha del evento y vincula el o los perfiles que van a estar repartiendo tarjetas. Así, todo lo que entre ese día queda automáticamente agrupado y medible por separado del resto de tus Leads normales.' },
      { t: 'h', c: 'Durante' },
      { t: 'p', c: 'Prioriza que te escaneen a ti (NFC o QR) por sobre escanear tú las tarjetas de otros: cada escaneo real que recibes es el que dispara tus Follow-ups automáticos, no al revés.' },
      { t: 'h', c: 'Después' },
      { t: 'p', c: 'Revisa el panel de Campañas para ver cuántos Leads generó el evento y en qué estado quedaron, y deja que los Follow-ups hagan el primer seguimiento por ti mientras repasas manualmente a quienes marcaste como urgentes en Leads.' },
    ],
  },
];

const ARTICLES_GUIAS_EN = [
  {
    slug: 'perfil-que-convierte',
    category: 'guias',
    title: 'Build a profile that converts',
    excerpt: 'What to prioritize so your card actually generates contacts.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Having an NFC card doesn’t generate results on its own: what turns a "tap" into a real contact is what the person finds on the other side. This is what matters most, in order:' },
      { t: 'steps', items: [
        'A real, professional photo. A profile with no photo (or a generic one) feels less trustworthy than one with a recognizable face.',
        'Your title and company, clear, without abbreviations only you understand.',
        'The 2 or 3 channels you actually want people to contact you through, not all 6 you have available: a profile crowded with buttons dilutes the one action you care about.',
        'At least one attached file if your field justifies it (a proposal, a catalog, a portfolio): it’s the difference between "they saved my contact" and "they walked away with something of yours".',
      ] },
      { t: 'p', c: 'After that, the template and the colors are the last 10%: they help the first impression, but they don’t replace the points above.' },
    ],
  },
  {
    slug: 'hub-vs-leads-vs-campanas',
    category: 'guias',
    title: 'Hub, Leads and Campaigns: when to use each one',
    excerpt: 'The three concepts people confuse most on the platform.',
    popular: true,
    blocks: [
      { t: 'p', c: 'They’re three related but different things, and the most common confusion is not being clear on which is which:' },
      { t: 'table', headers: ['Concept', 'What it is', 'How people end up there'], rows: [
        ['Leads', 'People who left their details through your public profile.', 'Automatic: anyone who fills out "Identify me".'],
        ['Hub', 'Your general address book inside Contact.', 'Manually, by promoting a Lead, or by scanning a physical card with the camera.'],
        ['Campaigns', 'A way to group and measure the Leads from a specific event.', 'Automatic for any Lead that comes in while the campaign is active.'],
      ] },
      { t: 'p', c: 'In other words: Leads is the inbox of new people, Hub is where your whole network lives once you’ve decided to keep that contact, and Campaigns is a lens for measuring Leads grouped by event: it doesn’t replace the other two, it organizes them.' },
    ],
  },
  {
    slug: 'sacar-provecho-evento',
    category: 'guias',
    title: 'How to make the most of an event with your card',
    excerpt: 'Before, during and after a trade show or talk.',
    blocks: [
      { t: 'h', c: 'Before' },
      { t: 'p', c: 'Create a Campaign with the event’s date and link the profile(s) that will be handing out cards. That way, everything that comes in that day is automatically grouped and measurable separately from the rest of your regular Leads.' },
      { t: 'h', c: 'During' },
      { t: 'p', c: 'Prioritize getting scanned yourself (NFC or QR) over scanning other people’s cards: every real scan you receive is what triggers your automatic Follow-ups, not the other way around.' },
      { t: 'h', c: 'After' },
      { t: 'p', c: 'Check the Campaigns panel to see how many Leads the event generated and what state they’re in, and let the Follow-ups handle the first round of outreach for you while you manually go through whoever you marked as urgent in Leads.' },
    ],
  },
];

const ARTICLES_GUIAS_PT = [
  {
    slug: 'perfil-que-convierte',
    category: 'guias',
    title: 'Monte um perfil que converte',
    excerpt: 'O que priorizar para que seu cartão realmente gere contatos.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Ter um cartão NFC não gera resultados por si só: o que transforma um "toque" em um contato real é o que a pessoa encontra do outro lado. É isso que tem mais impacto, em ordem:' },
      { t: 'steps', items: [
        'Uma foto real e profissional. Um perfil sem foto (ou com uma foto genérica) parece menos confiável do que um com um rosto reconhecível.',
        'Seu cargo e empresa claros, sem abreviações que só você entende.',
        'Os 2 ou 3 canais pelos quais você realmente quer ser contatado, não os 6 que você tem disponíveis: um perfil cheio de botões dilui a ação que importa para você.',
        'Pelo menos um arquivo anexado se o seu segmento justificar (uma proposta, um catálogo, um portfólio): é a diferença entre "salvaram meu contato" e "levaram algo seu".',
      ] },
      { t: 'p', c: 'Depois disso, o modelo e as cores são os últimos 10%: ajudam na primeira impressão, mas não substituem os pontos anteriores.' },
    ],
  },
  {
    slug: 'hub-vs-leads-vs-campanas',
    category: 'guias',
    title: 'Hub, Leads e Campanhas: quando usar cada um',
    excerpt: 'Os três conceitos que mais se confundem na plataforma.',
    popular: true,
    blocks: [
      { t: 'p', c: 'São três coisas relacionadas, porém distintas, e a confusão mais comum é não ter claro qual é qual:' },
      { t: 'table', headers: ['Conceito', 'O que é', 'Como as pessoas chegam lá'], rows: [
        ['Leads', 'Pessoas que deixaram seus dados pelo seu perfil público.', 'Automático: qualquer pessoa que preencha "Identificar-me".'],
        ['Hub', 'Sua agenda de contatos geral dentro do Contact.', 'Manualmente, promovendo um Lead, ou escaneando um cartão físico com a câmera.'],
        ['Campanhas', 'Uma forma de agrupar e medir os Leads de um evento específico.', 'Automático para qualquer Lead que entre enquanto a campanha estiver ativa.'],
      ] },
      { t: 'p', c: 'Em outras palavras: Leads é a caixa de entrada de gente nova, Hub é onde vive sua rede completa depois que você decidiu manter aquele contato, e Campanhas é uma lente para medir Leads agrupados por evento: não substitui os outros dois, organiza eles.' },
    ],
  },
  {
    slug: 'sacar-provecho-evento',
    category: 'guias',
    title: 'Como aproveitar um evento com seu cartão',
    excerpt: 'Antes, durante e depois de uma feira ou palestra.',
    blocks: [
      { t: 'h', c: 'Antes' },
      { t: 'p', c: 'Crie uma Campanha com a data do evento e vincule o(s) perfil(is) que vão distribuir cartões. Assim, tudo o que entrar naquele dia fica automaticamente agrupado e mensurável separadamente do resto dos seus Leads normais.' },
      { t: 'h', c: 'Durante' },
      { t: 'p', c: 'Priorize ser escaneado (NFC ou QR) em vez de escanear os cartões dos outros: cada escaneamento real que você recebe é o que dispara seus Follow-ups automáticos, não o contrário.' },
      { t: 'h', c: 'Depois' },
      { t: 'p', c: 'Confira o painel de Campanhas para ver quantos Leads o evento gerou e em que estado ficaram, e deixe os Follow-ups fazerem o primeiro acompanhamento por você enquanto você revisa manualmente quem você marcou como urgente em Leads.' },
    ],
  },
];

// ═══════════════════════════ LEADS ═══════════════════════════

const ARTICLES_LEADS_ES = [
  {
    slug: 'que-es-un-lead',
    category: 'leads',
    title: 'Qué es un Lead y cómo se genera',
    excerpt: 'El formulario "Identificarme" en tu perfil público.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Un Lead se genera cuando alguien que visita tu perfil público completa el formulario "Identificarme": básicamente, te deja sus propios datos de contacto. Es la vía de ida y vuelta: tú compartiste tu tarjeta, y ahora la otra persona te dejó la suya.' },
      { t: 'p', c: 'Cada Lead que recibes queda disponible en tu panel de Leads, con la fecha en que llegó y el perfil al que corresponde (si manejas más de uno).' },
    ],
  },
  {
    slug: 'gestionar-leads',
    category: 'leads',
    title: 'Gestiona tus Leads',
    excerpt: 'Las 3 columnas de estado y la urgencia por antigüedad.',
    blocks: [
      { t: 'p', c: 'El panel de Leads organiza a cada persona que te dejó sus datos en tres columnas: Nuevo, Contactado y No contactado. Puedes mover un Lead de columna a medida que avanzas en la conversación, y contactarlo directamente por teléfono o email con un clic desde la misma tarjeta.' },
      { t: 'callout', variant: 'warn', c: 'Un Lead que lleva más de 24 horas en "Nuevo" se marca como urgente, para que ningún contacto se te pierda por descuido.' },
      { t: 'p', c: 'Desde cualquier Lead también puedes enviarlo directamente a tu Hub, si ya decidiste que es un contacto que quieres seguir trabajando a largo plazo.' },
    ],
  },
  {
    slug: 'hub-crm',
    category: 'leads',
    title: 'Hub: tu CRM de contactos',
    excerpt: 'Agrega contactos manuales o promueve un Lead.',
    blocks: [
      { t: 'p', c: 'El Hub es tu libreta de contactos dentro de Contact, separada de los Leads que llegan por tu perfil público. Acá reúnes a toda la gente con la que haces networking, sea que te haya escaneado la tarjeta o no.' },
      { t: 'list', items: [
        'Puedes agregar un contacto manualmente, escribiendo sus datos tú mismo.',
        'Puedes promover un Lead existente al Hub cuando decides que vale la pena seguir ese contacto.',
        'Puedes escanear una tarjeta de presentación física con la cámara: ver el siguiente artículo.',
      ] },
    ],
  },
  {
    slug: 'escaneo-tarjetas-ia',
    category: 'leads',
    title: 'Escanea tarjetas físicas con IA',
    excerpt: 'Convierte una tarjeta de papel en un contacto digital.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Dentro del Hub puedes tomarle una foto a una tarjeta de presentación física que te hayan dado (de esas de papel) y nuestro sistema usa inteligencia artificial para leerla y extraer automáticamente el nombre, la empresa, el cargo, el email, el teléfono y el sitio web de esa persona, sin que tengas que escribir nada a mano.' },
      { t: 'p', c: 'Revisa los datos extraídos antes de guardar, por si la foto quedó borrosa o el diseño de la tarjeta dificulta la lectura automática.' },
    ],
  },
  {
    slug: 'followups-automaticos',
    category: 'leads',
    title: 'Follow-ups automáticos por email',
    excerpt: 'Una secuencia de 3 correos que se envían solos.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Follow-ups es una secuencia de 3 correos pensada para que ningún contacto real se te enfríe por falta de tiempo: Conexión, Seguimiento y Aporte de valor. Se activa únicamente cuando el contacto llega por un toque o escaneo real de tu tarjeta: nunca cuando agregas un contacto a mano en el Hub, porque ahí ya decidiste tú el siguiente paso.' },
      { t: 'h', c: 'Cómo se envían' },
      { t: 'list', items: [
        'El primer correo ("Conexión") se envía siempre que tengas la función activada.',
        'Los correos 2 y 3 se envían espaciados en el tiempo, y se cancelan automáticamente apenas la persona te responde: así nunca le vas a seguir escribiendo a alguien que ya te contestó.',
        'Los correos salen desde un alias con tu propio nombre (algo como tunombre@hola.contactnetwork.cl), nunca "a través de Contact": para quien lo recibe, el correo se siente 100% tuyo.',
      ] },
      { t: 'h', c: 'El tercer correo puede llevar un adjunto' },
      { t: 'p', c: 'El correo de "Aporte de valor" admite adjuntar un archivo: un caso de éxito, una guía, lo que tenga sentido para seguir la conversación.' },
      { t: 'p', c: 'Las plantillas de los 3 correos son editables, y puedes usar variables como tu nombre, tu empresa o el nombre del contacto para que cada envío se sienta personal. También puedes mandarte un correo de prueba antes de activar la secuencia.' },
    ],
  },
  {
    slug: 'campanas-eventos',
    category: 'leads',
    title: 'Campañas y Eventos',
    excerpt: 'Mide cuántos Leads te dejó cada evento al que vas.',
    blocks: [
      { t: 'p', c: 'Si repartes tu tarjeta en ferias, charlas o eventos, Campañas te deja crear un contenedor con fecha de inicio y término, vincular uno o varios perfiles a ese evento y, opcionalmente, un presupuesto. Mientras la campaña está activa, cualquier Lead que entre por un perfil vinculado queda automáticamente etiquetado con ese evento.' },
      { t: 'p', c: 'Con eso puedes ver, evento por evento: cuántos Leads generó, en qué estado quedaron, qué perfil fue el más efectivo, y si cargaste un presupuesto, el costo por Lead y el costo por Lead que efectivamente avanzó en tu proceso. Todo exportable a Excel.' },
    ],
  },
];

const ARTICLES_LEADS_EN = [
  {
    slug: 'que-es-un-lead',
    category: 'leads',
    title: 'What a Lead is and how it’s generated',
    excerpt: 'The "Identify me" form on your public profile.',
    popular: true,
    blocks: [
      { t: 'p', c: 'A Lead is generated when someone visiting your public profile fills out the "Identify me" form: basically, they leave you their own contact details. It’s the round-trip: you shared your card, and now the other person left you theirs.' },
      { t: 'p', c: 'Every Lead you receive becomes available in your Leads panel, with the date it arrived and which profile it belongs to (if you manage more than one).' },
    ],
  },
  {
    slug: 'gestionar-leads',
    category: 'leads',
    title: 'Manage your Leads',
    excerpt: 'The 3 status columns and urgency by age.',
    blocks: [
      { t: 'p', c: 'The Leads panel organizes everyone who left you their details into three columns: New, Contacted and Not contacted. You can move a Lead between columns as the conversation progresses, and contact them directly by phone or email with one click right from the card.' },
      { t: 'callout', variant: 'warn', c: 'A Lead that’s been in "New" for more than 24 hours gets marked urgent, so no contact slips through by accident.' },
      { t: 'p', c: 'From any Lead you can also send it straight to your Hub, once you’ve decided it’s a contact you want to keep working on long-term.' },
    ],
  },
  {
    slug: 'hub-crm',
    category: 'leads',
    title: 'Hub: your contact CRM',
    excerpt: 'Add manual contacts or promote a Lead.',
    blocks: [
      { t: 'p', c: 'The Hub is your address book inside Contact, separate from the Leads that come in through your public profile. This is where you gather everyone you network with, whether they scanned your card or not.' },
      { t: 'list', items: [
        'You can add a contact manually, typing in their details yourself.',
        'You can promote an existing Lead to the Hub once you decide that contact is worth following up on.',
        'You can scan a physical business card with the camera: see the next article.',
      ] },
    ],
  },
  {
    slug: 'escaneo-tarjetas-ia',
    category: 'leads',
    title: 'Scan physical cards with AI',
    excerpt: 'Turn a paper card into a digital contact.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Inside the Hub you can take a photo of a physical business card someone handed you (one of the paper ones), and our system uses AI to read it and automatically pull out that person’s name, company, title, email, phone and website, with nothing for you to type by hand.' },
      { t: 'p', c: 'Review the extracted data before saving, in case the photo came out blurry or the card’s design makes automatic reading harder.' },
    ],
  },
  {
    slug: 'followups-automaticos',
    category: 'leads',
    title: 'Automatic email Follow-ups',
    excerpt: 'A sequence of 3 emails that send themselves.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Follow-ups is a sequence of 3 emails designed so no real contact goes cold for lack of time: Connection, Follow-up, and Value add. It only activates when a contact comes in through a real tap or scan of your card: never when you add a contact by hand in the Hub, because there you’ve already decided the next step yourself.' },
      { t: 'h', c: 'How they’re sent' },
      { t: 'list', items: [
        'The first email ("Connection") always sends as long as the feature is enabled.',
        'Emails 2 and 3 go out spaced over time, and get cancelled automatically as soon as the person replies to you: so you’ll never keep emailing someone who already answered.',
        'The emails go out from an alias with your own name (something like yourname@hola.contactnetwork.cl), never "via Contact": to whoever receives it, the email feels 100% yours.',
      ] },
      { t: 'h', c: 'The third email can carry an attachment' },
      { t: 'p', c: 'The "Value add" email supports attaching a file: a case study, a guide, whatever makes sense to keep the conversation going.' },
      { t: 'p', c: 'All 3 email templates are editable, and you can use variables like your name, your company or the contact’s name so every send feels personal. You can also send yourself a test email before turning the sequence on.' },
    ],
  },
  {
    slug: 'campanas-eventos',
    category: 'leads',
    title: 'Campaigns & Events',
    excerpt: 'Measure how many Leads each event you attend generated.',
    blocks: [
      { t: 'p', c: 'If you hand out your card at trade shows, talks or events, Campaigns lets you create a container with a start and end date, link one or more profiles to that event and, optionally, a budget. While the campaign is active, any Lead that comes in through a linked profile is automatically tagged with that event.' },
      { t: 'p', c: 'With that you can see, event by event: how many Leads it generated, what state they’re in, which profile was most effective, and if you loaded a budget, the cost per Lead and the cost per Lead that actually moved forward in your process. All exportable to Excel.' },
    ],
  },
];

const ARTICLES_LEADS_PT = [
  {
    slug: 'que-es-un-lead',
    category: 'leads',
    title: 'O que é um Lead e como ele é gerado',
    excerpt: 'O formulário "Identificar-me" no seu perfil público.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Um Lead é gerado quando alguém que visita seu perfil público preenche o formulário "Identificar-me": basicamente, deixa os próprios dados de contato para você. É a via de ida e volta: você compartilhou seu cartão, e agora a outra pessoa deixou o dela.' },
      { t: 'p', c: 'Cada Lead que você recebe fica disponível no seu painel de Leads, com a data em que chegou e o perfil correspondente (se você gerencia mais de um).' },
    ],
  },
  {
    slug: 'gestionar-leads',
    category: 'leads',
    title: 'Gerencie seus Leads',
    excerpt: 'As 3 colunas de status e a urgência por tempo de espera.',
    blocks: [
      { t: 'p', c: 'O painel de Leads organiza cada pessoa que deixou seus dados em três colunas: Novo, Contatado e Não contatado. Você pode mover um Lead de coluna conforme avança na conversa, e entrar em contato diretamente por telefone ou email com um clique a partir do próprio cartão.' },
      { t: 'callout', variant: 'warn', c: 'Um Lead que fica mais de 24 horas em "Novo" é marcado como urgente, para que nenhum contato se perca por descuido.' },
      { t: 'p', c: 'A partir de qualquer Lead você também pode enviá-lo direto para seu Hub, quando decidir que é um contato que vale a pena acompanhar a longo prazo.' },
    ],
  },
  {
    slug: 'hub-crm',
    category: 'leads',
    title: 'Hub: seu CRM de contatos',
    excerpt: 'Adicione contatos manuais ou promova um Lead.',
    blocks: [
      { t: 'p', c: 'O Hub é sua agenda de contatos dentro do Contact, separada dos Leads que chegam pelo seu perfil público. Aqui você reúne todas as pessoas com quem você faz networking, tenham escaneado seu cartão ou não.' },
      { t: 'list', items: [
        'Você pode adicionar um contato manualmente, digitando os dados você mesmo.',
        'Você pode promover um Lead existente para o Hub quando decidir que vale a pena acompanhar esse contato.',
        'Você pode escanear um cartão de visita físico com a câmera: veja o próximo artigo.',
      ] },
    ],
  },
  {
    slug: 'escaneo-tarjetas-ia',
    category: 'leads',
    title: 'Escaneie cartões físicos com IA',
    excerpt: 'Transforme um cartão de papel em um contato digital.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Dentro do Hub você pode fotografar um cartão de visita físico que alguém te deu (daqueles de papel), e nosso sistema usa inteligência artificial para lê-lo e extrair automaticamente o nome, a empresa, o cargo, o email, o telefone e o site dessa pessoa, sem que você precise digitar nada.' },
      { t: 'p', c: 'Revise os dados extraídos antes de salvar, caso a foto tenha ficado desfocada ou o design do cartão dificulte a leitura automática.' },
    ],
  },
  {
    slug: 'followups-automaticos',
    category: 'leads',
    title: 'Follow-ups automáticos por email',
    excerpt: 'Uma sequência de 3 emails que são enviados sozinhos.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Follow-ups é uma sequência de 3 emails pensada para que nenhum contato real esfrie por falta de tempo: Conexão, Acompanhamento e Valor agregado. Ela é ativada apenas quando o contato chega por um toque ou escaneamento real do seu cartão: nunca quando você adiciona um contato manualmente no Hub, porque aí você mesmo já decidiu o próximo passo.' },
      { t: 'h', c: 'Como são enviados' },
      { t: 'list', items: [
        'O primeiro email ("Conexão") é enviado sempre que você tiver a função ativada.',
        'Os emails 2 e 3 são enviados espaçados no tempo, e são cancelados automaticamente logo que a pessoa responde: assim você nunca vai continuar escrevendo para alguém que já te respondeu.',
        'Os emails saem de um alias com seu próprio nome (algo como seunome@hola.contactnetwork.cl), nunca "através do Contact": para quem recebe, o email parece 100% seu.',
      ] },
      { t: 'h', c: 'O terceiro email pode ter um anexo' },
      { t: 'p', c: 'O email de "Valor agregado" permite anexar um arquivo: um case de sucesso, um guia, o que fizer sentido para continuar a conversa.' },
      { t: 'p', c: 'Os modelos dos 3 emails são editáveis, e você pode usar variáveis como seu nome, sua empresa ou o nome do contato para que cada envio pareça pessoal. Você também pode enviar um email de teste para você mesmo antes de ativar a sequência.' },
    ],
  },
  {
    slug: 'campanas-eventos',
    category: 'leads',
    title: 'Campanhas e Eventos',
    excerpt: 'Meça quantos Leads cada evento que você participou gerou.',
    blocks: [
      { t: 'p', c: 'Se você distribui seu cartão em feiras, palestras ou eventos, Campanhas permite criar um conjunto com data de início e término, vincular um ou mais perfis a esse evento e, opcionalmente, um orçamento. Enquanto a campanha estiver ativa, qualquer Lead que entrar por um perfil vinculado fica automaticamente marcado com esse evento.' },
      { t: 'p', c: 'Com isso você pode ver, evento por evento: quantos Leads ele gerou, em que estado ficaram, qual perfil foi mais eficaz, e se você carregou um orçamento, o custo por Lead e o custo por Lead que realmente avançou no seu processo. Tudo exportável para Excel.' },
    ],
  },
];

// ═══════════════════════════ ANALÍTICA / ANALYTICS / ANALYTICS ═══════════════════════════

const ARTICLES_ANALITICA_ES = [
  {
    slug: 'metricas-disponibles',
    category: 'analitica',
    title: 'Métricas disponibles',
    excerpt: 'Qué mide cada KPI, y qué es gratis y qué es de pago.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Cada vez que alguien visita tu perfil, hace clic en una de tus redes, guarda tu contacto o descarga tu pase de Apple Wallet, queda registrado como un evento. Sobre esos eventos se calculan todas tus métricas.' },
      { t: 'h', c: 'Siempre disponibles (todos los planes)' },
      { t: 'list', items: [
        'Visitas totales a tu perfil.',
        'Clics en tus redes sociales.',
        'Contactos guardados (cuántas veces se descargó tu ficha de contacto).',
        'Tasa de interacción (% de visitas que terminan en algún clic o guardado).',
      ] },
      { t: 'callout', variant: 'pro', c: 'Tendencia en el tiempo, rendimiento por cada red social, distribución de interacciones y cuál es tu red más efectiva están disponibles desde el plan Pro.' },
    ],
  },
  {
    slug: 'insights-ia',
    category: 'analitica',
    title: 'Insights con Inteligencia Artificial',
    excerpt: 'Recomendaciones automáticas sobre tu perfil.',
    blocks: [
      { t: 'p', c: 'Insights analiza el comportamiento de tus visitantes y te entrega lecturas y recomendaciones en lenguaje simple: por ejemplo, si una red social en particular está generando mucho más interés que las demás, o si tu tasa de interacción bajó respecto al periodo anterior. Cada insight viene con una prioridad (alta, media o baja) para que sepas en qué enfocarte primero.' },
      { t: 'p', c: 'La idea de fondo es ahorrarte el trabajo de interpretar los números tú mismo: en vez de mirar un gráfico de tendencia y preguntarte qué significa, Insights te lo traduce directamente a una recomendación accionable, como mover el foco de tu perfil hacia la red social que mejor te está funcionando.' },
    ],
  },
  {
    slug: 'analitica-de-equipo',
    category: 'analitica',
    title: 'Analítica de Equipo',
    excerpt: 'El rendimiento combinado de todos los perfiles de tu empresa.',
    blocks: [
      { t: 'p', c: 'Si administras varios perfiles bajo una cuenta Enterprise, Analítica de Equipo te muestra el panorama combinado: visitas de los últimos 14 días, clics por red social a nivel de todo el equipo, y leads agrupados por estado. Incluye además un ranking de los miembros del equipo según las visitas que generó cada uno.' },
    ],
  },
  {
    slug: 'exportar-reportes',
    category: 'analitica',
    title: 'Exporta tus reportes',
    excerpt: 'PDF y Excel con tus números listos para compartir.',
    blocks: [
      { t: 'p', c: 'Puedes exportar un reporte ejecutivo de tu desempeño en PDF o Excel: útil para compartir resultados con tu equipo o tu jefatura sin tener que explicar cómo leer el panel.' },
    ],
  },
];

const ARTICLES_ANALITICA_EN = [
  {
    slug: 'metricas-disponibles',
    category: 'analitica',
    title: 'Available metrics',
    excerpt: 'What each KPI measures, and what’s free vs. paid.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Every time someone visits your profile, clicks one of your social networks, saves your contact, or downloads your Apple Wallet pass, it gets logged as an event. All of your metrics are calculated from those events.' },
      { t: 'h', c: 'Always available (every plan)' },
      { t: 'list', items: [
        'Total visits to your profile.',
        'Clicks on your social networks.',
        'Contacts saved (how many times your contact card was downloaded).',
        'Engagement rate (% of visits that end in a click or a save).',
      ] },
      { t: 'callout', variant: 'pro', c: 'Trend over time, performance per social network, interaction breakdown and which network is most effective for you are available from the Pro plan up.' },
    ],
  },
  {
    slug: 'insights-ia',
    category: 'analitica',
    title: 'AI Insights',
    excerpt: 'Automatic recommendations about your profile.',
    blocks: [
      { t: 'p', c: 'Insights analyzes your visitors’ behavior and gives you readings and recommendations in plain language: for example, if one particular social network is generating much more interest than the rest, or if your engagement rate dropped compared to the previous period. Each insight comes with a priority (high, medium or low) so you know what to focus on first.' },
      { t: 'p', c: 'The underlying idea is to save you the work of interpreting the numbers yourself: instead of looking at a trend chart and wondering what it means, Insights translates it directly into an actionable recommendation, like shifting your profile’s focus toward the social network that’s working best for you.' },
    ],
  },
  {
    slug: 'analitica-de-equipo',
    category: 'analitica',
    title: 'Team Analytics',
    excerpt: 'The combined performance of every profile in your company.',
    blocks: [
      { t: 'p', c: 'If you manage several profiles under an Enterprise account, Team Analytics shows you the combined picture: visits over the last 14 days, clicks by social network across the whole team, and leads grouped by status. It also includes a ranking of team members by the visits each one generated.' },
    ],
  },
  {
    slug: 'exportar-reportes',
    category: 'analitica',
    title: 'Export your reports',
    excerpt: 'PDF and Excel with your numbers, ready to share.',
    blocks: [
      { t: 'p', c: 'You can export an executive report of your performance as PDF or Excel: useful for sharing results with your team or management without having to explain how to read the dashboard.' },
    ],
  },
];

const ARTICLES_ANALITICA_PT = [
  {
    slug: 'metricas-disponibles',
    category: 'analitica',
    title: 'Métricas disponíveis',
    excerpt: 'O que cada KPI mede, e o que é gratuito e o que é pago.',
    popular: true,
    blocks: [
      { t: 'p', c: 'Toda vez que alguém visita seu perfil, clica em uma das suas redes, salva seu contato ou faz download do seu passe do Apple Wallet, isso é registrado como um evento. Todas as suas métricas são calculadas a partir desses eventos.' },
      { t: 'h', c: 'Sempre disponíveis (todos os planos)' },
      { t: 'list', items: [
        'Total de visitas ao seu perfil.',
        'Cliques nas suas redes sociais.',
        'Contatos salvos (quantas vezes sua ficha de contato foi baixada).',
        'Taxa de interação (% de visitas que terminam em um clique ou salvamento).',
      ] },
      { t: 'callout', variant: 'pro', c: 'Tendência ao longo do tempo, desempenho por rede social, distribuição de interações e qual é sua rede mais eficaz estão disponíveis a partir do plano Pro.' },
    ],
  },
  {
    slug: 'insights-ia',
    category: 'analitica',
    title: 'Insights com Inteligência Artificial',
    excerpt: 'Recomendações automáticas sobre seu perfil.',
    blocks: [
      { t: 'p', c: 'O Insights analisa o comportamento dos seus visitantes e entrega leituras e recomendações em linguagem simples: por exemplo, se uma rede social em particular está gerando muito mais interesse que as outras, ou se sua taxa de interação caiu em relação ao período anterior. Cada insight vem com uma prioridade (alta, média ou baixa) para você saber onde focar primeiro.' },
      { t: 'p', c: 'A ideia por trás disso é poupar você do trabalho de interpretar os números sozinho: em vez de olhar um gráfico de tendência e se perguntar o que ele significa, o Insights traduz isso diretamente em uma recomendação acionável, como mover o foco do seu perfil para a rede social que está funcionando melhor para você.' },
    ],
  },
  {
    slug: 'analitica-de-equipo',
    category: 'analitica',
    title: 'Analytics de Equipe',
    excerpt: 'O desempenho combinado de todos os perfis da sua empresa.',
    blocks: [
      { t: 'p', c: 'Se você administra vários perfis em uma conta Enterprise, o Analytics de Equipe mostra o panorama combinado: visitas dos últimos 14 dias, cliques por rede social em nível de toda a equipe, e leads agrupados por status. Inclui também um ranking dos membros da equipe de acordo com as visitas que cada um gerou.' },
    ],
  },
  {
    slug: 'exportar-reportes',
    category: 'analitica',
    title: 'Exporte seus relatórios',
    excerpt: 'PDF e Excel com seus números prontos para compartilhar.',
    blocks: [
      { t: 'p', c: 'Você pode exportar um relatório executivo do seu desempenho em PDF ou Excel: útil para compartilhar resultados com sua equipe ou sua chefia sem precisar explicar como ler o painel.' },
    ],
  },
];

// ═══════════════════════════ PROBLEMAS / TROUBLESHOOTING / PROBLEMAS ═══════════════════════════

const ARTICLES_PROBLEMAS_ES = [
  {
    slug: 'tarjeta-no-abre',
    category: 'problemas',
    title: 'Mi tarjeta no abre el perfil al acercarla',
    excerpt: 'Las causas más comunes, de la más a la menos probable.',
    popular: true,
    blocks: [
      { t: 'steps', items: [
        'Confirma que el NFC del celular que está acercando la tarjeta esté activado: en Android a veces viene apagado en Ajustes; en iPhone viene activado por defecto.',
        'Prueba acercar la tarjeta más lento y en el centro de la parte trasera del celular (el lector NFC suele estar ahí, no en las esquinas).',
        'Si no funciona con NFC, usa el código QR impreso en la misma tarjeta: abre exactamente el mismo perfil y no depende de nada del paso anterior.',
        'Si el QR tampoco escanea, revisa el siguiente artículo.',
        'Si después de todo esto sigue sin funcionar, puede ser un problema de fabricación de esa tarjeta puntual: escríbenos desde Soporte y la reponemos.',
      ] },
    ],
  },
  {
    slug: 'qr-no-escanea',
    category: 'problemas',
    title: 'El código QR no escanea',
    excerpt: 'Casi siempre es algo del entorno, no del código.',
    blocks: [
      { t: 'list', items: [
        'Asegúrate de tener buena luz: los reflejos sobre la tarjeta son la causa más común de que la cámara no logre leerlo.',
        'Aleja o acerca el celular un poco; la mayoría de las apps de cámara necesitan que el código ocupe un rango de distancia específico para enfocar bien.',
        'Usa la app de Cámara nativa del celular en vez de una app de QR de terceros: tanto iPhone como Android modernos reconocen códigos QR directamente desde la cámara, sin instalar nada.',
      ] },
    ],
  },
  {
    slug: 'no-llega-correo',
    category: 'problemas',
    title: 'No me llegó un correo (Follow-up o recordatorio)',
    excerpt: 'Por qué puede pasar, y qué es normal que no llegue.',
    blocks: [
      { t: 'p', c: 'Antes de pensar que algo está roto, vale la pena entender cómo funciona realmente la secuencia de Follow-ups: el primer correo ("Conexión") se envía siempre que la función esté activada, pero los correos 2 y 3 ("Seguimiento" y "Aporte de valor") solo se envían si la detección de respuestas está configurada a nivel de tu cuenta: si no lo está, esos correos se omiten intencionalmente, no es un error.' },
      { t: 'callout', variant: 'info', c: 'Si el contacto te respondió el primer correo, lo normal es que los siguientes de la secuencia se cancelen automáticamente: eso tampoco es un error, es la secuencia funcionando como debería.' },
      { t: 'p', c: 'También recuerda que un contacto agregado a mano en el Hub nunca dispara Follow-ups: solo lo hace un escaneo real (NFC, QR, o un escaneo de tarjeta física en el Hub).' },
      { t: 'p', c: 'Si después de revisar esto sigues sin ver el correo que esperabas, escríbenos desde Soporte con el nombre del contacto y la fecha aproximada para revisarlo puntualmente.' },
    ],
  },
  {
    slug: 'no-puedo-iniciar-sesion',
    category: 'problemas',
    title: 'No puedo iniciar sesión',
    excerpt: 'Contraseña, intentos fallidos y recuperación de cuenta.',
    blocks: [
      { t: 'list', items: [
        'Si olvidaste tu contraseña, usa "¿Olvidaste tu contraseña?" en la pantalla de inicio de sesión: te llega un correo para definir una nueva.',
        'Después de varios intentos seguidos con la contraseña incorrecta, el inicio de sesión se bloquea temporalmente por unos minutos como medida de seguridad: espera un momento y vuelve a intentar.',
        'Confirma que estás usando el mismo correo con el que te registraste originalmente; si tienes dudas, prueba primero con "¿Olvidaste tu contraseña?", que te va a confirmar si ese correo existe en el sistema.',
      ] },
    ],
  },
];

const ARTICLES_PROBLEMAS_EN = [
  {
    slug: 'tarjeta-no-abre',
    category: 'problemas',
    title: 'My card doesn’t open the profile when tapped',
    excerpt: 'The most common causes, from most to least likely.',
    popular: true,
    blocks: [
      { t: 'steps', items: [
        'Confirm that NFC is enabled on the phone tapping the card: on Android it’s sometimes off in Settings; on iPhone it’s on by default.',
        'Try tapping the card more slowly and at the center of the phone’s back (the NFC reader is usually there, not in the corners).',
        'If NFC doesn’t work, use the QR code printed on the same card: it opens exactly the same profile and doesn’t depend on anything from the step above.',
        'If the QR doesn’t scan either, check the next article.',
        'If it still doesn’t work after all of this, it might be a manufacturing issue with that specific card: write to us from Support and we’ll replace it.',
      ] },
    ],
  },
  {
    slug: 'qr-no-escanea',
    category: 'problemas',
    title: 'The QR code won’t scan',
    excerpt: 'It’s almost always something about the environment, not the code.',
    blocks: [
      { t: 'list', items: [
        'Make sure you have good light: glare on the card is the most common reason the camera can’t read it.',
        'Move the phone a little closer or farther away; most camera apps need the code to sit within a specific distance range to focus properly.',
        'Use the phone’s native Camera app instead of a third-party QR app: both modern iPhones and Androids recognize QR codes directly from the camera, with nothing to install.',
      ] },
    ],
  },
  {
    slug: 'no-llega-correo',
    category: 'problemas',
    title: 'I didn’t get an email (Follow-up or reminder)',
    excerpt: 'Why it can happen, and what’s normal to not receive.',
    blocks: [
      { t: 'p', c: 'Before assuming something’s broken, it’s worth understanding how the Follow-up sequence actually works: the first email ("Connection") sends as long as the feature is enabled, but emails 2 and 3 ("Follow-up" and "Value add") only send if reply detection is configured at the account level: if it isn’t, those emails are skipped on purpose, that’s not a bug.' },
      { t: 'callout', variant: 'info', c: 'If the contact replied to the first email, it’s normal for the rest of the sequence to cancel automatically: that’s not a bug either, it’s the sequence working as intended.' },
      { t: 'p', c: 'Also remember a contact added by hand in the Hub never triggers Follow-ups: only a real scan does (NFC, QR, or a physical-card scan in the Hub).' },
      { t: 'p', c: 'If after checking all this you’re still missing an email you expected, write to us from Support with the contact’s name and the approximate date so we can look into it specifically.' },
    ],
  },
  {
    slug: 'no-puedo-iniciar-sesion',
    category: 'problemas',
    title: 'I can’t log in',
    excerpt: 'Password, failed attempts and account recovery.',
    blocks: [
      { t: 'list', items: [
        'If you forgot your password, use "Forgot your password?" on the login screen: you’ll get an email to set a new one.',
        'After several failed attempts in a row with the wrong password, login gets temporarily locked for a few minutes as a security measure: wait a moment and try again.',
        'Confirm you’re using the same email you originally signed up with; if you’re not sure, try "Forgot your password?" first — it will confirm whether that email exists in the system.',
      ] },
    ],
  },
];

const ARTICLES_PROBLEMAS_PT = [
  {
    slug: 'tarjeta-no-abre',
    category: 'problemas',
    title: 'Meu cartão não abre o perfil ao aproximar',
    excerpt: 'As causas mais comuns, da mais à menos provável.',
    popular: true,
    blocks: [
      { t: 'steps', items: [
        'Confirme que o NFC do celular que está aproximando o cartão está ativado: no Android, às vezes vem desligado nas Configurações; no iPhone vem ativado por padrão.',
        'Tente aproximar o cartão mais lentamente e no centro da parte de trás do celular (o leitor NFC geralmente fica aí, não nos cantos).',
        'Se não funcionar com NFC, use o código QR impresso no próprio cartão: ele abre exatamente o mesmo perfil e não depende de nada do passo anterior.',
        'Se o QR também não escanear, veja o próximo artigo.',
        'Se depois de tudo isso ainda não funcionar, pode ser um problema de fabricação daquele cartão específico: escreva para nós pelo Suporte e nós o substituímos.',
      ] },
    ],
  },
  {
    slug: 'qr-no-escanea',
    category: 'problemas',
    title: 'O código QR não escaneia',
    excerpt: 'Quase sempre é algo do ambiente, não do código.',
    blocks: [
      { t: 'list', items: [
        'Garanta que há boa luz: reflexos no cartão são a causa mais comum de a câmera não conseguir ler.',
        'Afaste ou aproxime um pouco o celular; a maioria dos apps de câmera precisa que o código fique dentro de uma faixa de distância específica para focar bem.',
        'Use o app de Câmera nativo do celular em vez de um app de QR de terceiros: tanto iPhone quanto Android modernos reconhecem códigos QR direto da câmera, sem instalar nada.',
      ] },
    ],
  },
  {
    slug: 'no-llega-correo',
    category: 'problemas',
    title: 'Não recebi um email (Follow-up ou lembrete)',
    excerpt: 'Por que isso pode acontecer, e o que é normal não chegar.',
    blocks: [
      { t: 'p', c: 'Antes de pensar que algo está quebrado, vale entender como a sequência de Follow-ups realmente funciona: o primeiro email ("Conexão") é enviado sempre que a função estiver ativada, mas os emails 2 e 3 ("Acompanhamento" e "Valor agregado") só são enviados se a detecção de respostas estiver configurada no nível da sua conta: se não estiver, esses emails são omitidos de propósito, não é um erro.' },
      { t: 'callout', variant: 'info', c: 'Se o contato respondeu ao primeiro email, é normal que os próximos da sequência sejam cancelados automaticamente: isso também não é um erro, é a sequência funcionando como deveria.' },
      { t: 'p', c: 'Lembre-se também que um contato adicionado manualmente no Hub nunca dispara Follow-ups: só um escaneamento real faz isso (NFC, QR, ou um escaneamento de cartão físico no Hub).' },
      { t: 'p', c: 'Se depois de revisar tudo isso você ainda não vê o email que esperava, escreva para nós pelo Suporte com o nome do contato e a data aproximada para revisarmos especificamente.' },
    ],
  },
  {
    slug: 'no-puedo-iniciar-sesion',
    category: 'problemas',
    title: 'Não consigo fazer login',
    excerpt: 'Senha, tentativas falhas e recuperação de conta.',
    blocks: [
      { t: 'list', items: [
        'Se você esqueceu sua senha, use "Esqueceu sua senha?" na tela de login: você recebe um email para definir uma nova.',
        'Depois de várias tentativas seguidas com a senha incorreta, o login é bloqueado temporariamente por alguns minutos como medida de segurança: espere um momento e tente novamente.',
        'Confirme que você está usando o mesmo email com o qual se cadastrou originalmente; se tiver dúvidas, tente primeiro "Esqueceu sua senha?", que vai confirmar se esse email existe no sistema.',
      ] },
    ],
  },
];

// ═══════════════════════════ CUENTA / ACCOUNT / CONTA ═══════════════════════════

const ARTICLES_CUENTA_ES = [
  {
    slug: 'planes',
    category: 'cuenta',
    title: 'Planes disponibles: Free, Pro y Enterprise',
    excerpt: 'Tabla comparativa completa.',
    popular: true,
    blocks: [
      { t: 'table', headers: ['', 'Free', 'Pro', 'Enterprise'], rows: [
        ['Perfiles', '1', '3', '10'],
        ['Leads', 'Hasta 5 por mes', 'Ilimitados', 'Ilimitados'],
        ['Analíticas', '4 KPIs básicos', 'Completas + Insights con IA', 'Completas + Analítica de Equipo'],
        ['Imagen de fondo personalizada', 'No', 'Sí', 'Sí'],
        ['Quitar marca de agua', 'No', 'Sí', 'Sí'],
        ['Hub (CRM)', 'No', 'Sí', 'Sí'],
        ['Campañas y Eventos', 'No', 'Sí', 'Sí'],
        ['Follow-ups automáticos', 'No', 'Sí', 'Sí'],
        ['Exportar reportes', 'No', 'Sí', 'Sí'],
        ['Cotizaciones', 'No', 'Sí', 'Sí'],
        ['Google Sheets', 'No', 'Sí', 'Sí'],
        ['API pública y Webhooks', 'No', 'No', 'Sí'],
        ['Equipos y llaves de acceso', 'No', 'No', 'Sí'],
        ['Analítica de Equipo', 'No', 'No', 'Sí'],
        ['Brand Kit', 'No', 'No', 'Sí'],
      ] },
      { t: 'p', c: 'El plan Pro se cobra $2.500 CLP al mes, o $25.000 CLP al año (equivalente a 2 meses gratis si pagas anual). Enterprise no tiene un precio fijo publicado: se cotiza según el tamaño de tu equipo.' },
    ],
  },
  {
    slug: 'gestionar-suscripcion',
    category: 'cuenta',
    title: 'Gestiona tu suscripción',
    excerpt: 'Cambiar de plan, pausar, cancelar.',
    blocks: [
      { t: 'p', c: 'Desde Configuración → Plan puedes subir de Free a Pro, cambiar entre pago mensual y anual, pausar tu suscripción (se mantiene vigente pero sin cobrarte hasta que la reactives) o cancelarla. Si cancelas, mantienes el acceso a las funciones de tu plan hasta el final del periodo que ya pagaste: no pierdes lo que ya pagaste a mitad de camino.' },
      { t: 'p', c: 'El cobro recurrente se procesa con MercadoPago. Si por alguna razón un cobro no se procesa correctamente, tu cuenta vuelve automáticamente a Free hasta que se regularice.' },
    ],
  },
  {
    slug: 'configuracion-cuenta',
    category: 'cuenta',
    title: 'Configuración de tu cuenta',
    excerpt: 'Idioma, contraseña y eliminar tu cuenta.',
    blocks: [
      { t: 'p', c: 'En Configuración → Cuenta puedes cambiar el idioma del panel, tu nombre y tu contraseña.' },
      { t: 'callout', variant: 'warn', c: 'En la "Zona peligrosa" de Configuración puedes eliminar tu cuenta por completo. Esta acción borra tu perfil, tus Leads, tus Analíticas y cualquier dato asociado, y no se puede deshacer.' },
    ],
  },
  {
    slug: 'equipos',
    category: 'cuenta',
    title: 'Equipos: perfiles y accesos',
    excerpt: 'Una cuenta, varios perfiles, cada uno con su propio acceso.',
    blocks: [
      { t: 'p', c: 'Equipos está pensado para empresas que quieren dar una tarjeta Contact a varias personas bajo una sola cuenta administrada centralmente. Desde el panel de Equipos defines datos de la empresa (rubro, descripción) y administras a cada miembro.' },
      { t: 'p', c: 'Cada miembro del equipo recibe su propia llave de acceso, un inicio de sesión independiente ligado específicamente a su perfil: así cada persona puede entrar a actualizar su propia información sin ver ni tocar la de sus compañeros, mientras tú mantienes la administración general desde la cuenta principal.' },
    ],
  },
  {
    slug: 'seguridad-privacidad',
    category: 'cuenta',
    title: 'Seguridad y privacidad de tus datos',
    excerpt: 'Cómo protegemos tu cuenta y la información de tus contactos.',
    blocks: [
      { t: 'p', c: 'Tu contraseña nunca se guarda en texto plano: se almacena con un hash criptográfico que hace imposible recuperarla en texto original, ni siquiera para nuestro propio equipo. Iniciar sesión genera un token de sesión (JWT) que viaja de forma segura entre tu navegador y nuestros servidores.' },
      { t: 'p', c: 'Si conectas Google Sheets, Contact solo puede crear y escribir en una planilla nueva que genera para ese propósito: nunca accede a otras planillas ni archivos existentes en tu Drive.' },
      { t: 'p', c: 'Si usas Webhooks (Enterprise), cada entrega va firmada con una clave única por webhook (HMAC) para que tu sistema pueda verificar que el envío realmente vino de Contact, y exigimos que la URL de destino sea HTTPS: nunca se permite una URL sin cifrar ni que apunte a una red interna.' },
      { t: 'callout', variant: 'info', c: 'Si alguna vez sospechas que una contraseña o una API Key se vio comprometida, puedes cambiarla o revocarla al instante desde Configuración o el panel de Desarrollador, sin tener que escribirnos primero.' },
    ],
  },
  {
    slug: 'brand-kit',
    category: 'cuenta',
    title: 'Brand Kit: identidad visual de tu empresa',
    excerpt: 'Una plantilla y colores únicos para todo el equipo.',
    blocks: [
      { t: 'p', c: 'Brand Kit te deja definir una plantilla y una paleta de colores corporativos una sola vez, y que se hereden automáticamente en los perfiles de todos los miembros de tu equipo: así todas las tarjetas de tu empresa se ven consistentes entre sí, sin depender de que cada persona configure bien su propia apariencia.' },
      { t: 'p', c: 'Cuando Brand Kit está activo, la personalización individual de plantilla y color en el perfil de cada miembro queda bloqueada, precisamente para proteger esa consistencia de marca.' },
    ],
  },
  {
    slug: 'soporte',
    category: 'cuenta',
    title: 'Soporte: cómo contactarnos',
    excerpt: 'A quién escribirle según tu duda.',
    blocks: [
      { t: 'p', c: 'Si algo no funciona como esperabas o tienes una duda puntual sobre tu cuenta, el canal más directo es la pestaña Soporte dentro de Configuración, en tu panel: desde ahí llega directo a nuestro equipo.' },
      { t: 'p', c: 'Si todavía no tienes cuenta y solo quieres resolver una pregunta rápida antes de comprar (precios, envíos, compatibilidad), puedes escribirle a nuestro asistente en el sitio público: lo vas a encontrar como una burbuja de chat en la esquina inferior de la página.' },
    ],
  },
];

const ARTICLES_CUENTA_EN = [
  {
    slug: 'planes',
    category: 'cuenta',
    title: 'Available plans: Free, Pro and Enterprise',
    excerpt: 'Full comparison table.',
    popular: true,
    blocks: [
      { t: 'table', headers: ['', 'Free', 'Pro', 'Enterprise'], rows: [
        ['Profiles', '1', '3', '10'],
        ['Leads', 'Up to 5 per month', 'Unlimited', 'Unlimited'],
        ['Analytics', '4 basic KPIs', 'Full + AI Insights', 'Full + Team Analytics'],
        ['Custom background image', 'No', 'Yes', 'Yes'],
        ['Remove watermark', 'No', 'Yes', 'Yes'],
        ['Hub (CRM)', 'No', 'Yes', 'Yes'],
        ['Campaigns & Events', 'No', 'Yes', 'Yes'],
        ['Automatic Follow-ups', 'No', 'Yes', 'Yes'],
        ['Export reports', 'No', 'Yes', 'Yes'],
        ['Quotes', 'No', 'Yes', 'Yes'],
        ['Google Sheets', 'No', 'Yes', 'Yes'],
        ['Public API & Webhooks', 'No', 'No', 'Yes'],
        ['Teams & access keys', 'No', 'No', 'Yes'],
        ['Team Analytics', 'No', 'No', 'Yes'],
        ['Brand Kit', 'No', 'No', 'Yes'],
      ] },
      { t: 'p', c: 'The Pro plan costs $2,500 CLP/month, or $25,000 CLP/year (equivalent to 2 months free if you pay annually). Enterprise has no fixed published price: it’s quoted based on your team’s size.' },
    ],
  },
  {
    slug: 'gestionar-suscripcion',
    category: 'cuenta',
    title: 'Manage your subscription',
    excerpt: 'Change plan, pause, cancel.',
    blocks: [
      { t: 'p', c: 'From Settings → Plan you can upgrade from Free to Pro, switch between monthly and annual billing, pause your subscription (it stays active but you won’t be charged until you reactivate it) or cancel it. If you cancel, you keep access to your plan’s features until the end of the period you already paid for: you don’t lose what you already paid for halfway through.' },
      { t: 'p', c: 'Recurring billing is processed through MercadoPago. If a charge fails to process for any reason, your account automatically drops back to Free until it’s resolved.' },
    ],
  },
  {
    slug: 'configuracion-cuenta',
    category: 'cuenta',
    title: 'Your account settings',
    excerpt: 'Language, password and deleting your account.',
    blocks: [
      { t: 'p', c: 'In Settings → Account you can change the dashboard’s language, your name and your password.' },
      { t: 'callout', variant: 'warn', c: 'In the Settings "Danger zone" you can delete your account entirely. This action erases your profile, your Leads, your Analytics and any related data, and it cannot be undone.' },
    ],
  },
  {
    slug: 'equipos',
    category: 'cuenta',
    title: 'Teams: profiles and access',
    excerpt: 'One account, several profiles, each with its own access.',
    blocks: [
      { t: 'p', c: 'Teams is built for companies that want to give a Contact card to several people under a single, centrally-managed account. From the Teams panel you set the company’s details (industry, description) and manage each member.' },
      { t: 'p', c: 'Every team member gets their own access key, an independent login tied specifically to their profile: that way each person can log in and update their own information without seeing or touching their teammates’, while you keep overall management from the main account.' },
    ],
  },
  {
    slug: 'seguridad-privacidad',
    category: 'cuenta',
    title: 'Security and privacy of your data',
    excerpt: 'How we protect your account and your contacts’ information.',
    blocks: [
      { t: 'p', c: 'Your password is never stored in plain text: it’s stored with a cryptographic hash that makes it impossible to recover the original text, not even for our own team. Logging in generates a session token (JWT) that travels securely between your browser and our servers.' },
      { t: 'p', c: 'If you connect Google Sheets, Contact can only create and write to a new spreadsheet it generates for that purpose: it never accesses other spreadsheets or existing files in your Drive.' },
      { t: 'p', c: 'If you use Webhooks (Enterprise), every delivery is signed with a key unique to that webhook (HMAC) so your system can verify the request really came from Contact, and we require the destination URL to be HTTPS: an unencrypted URL or one pointing to an internal network is never allowed.' },
      { t: 'callout', variant: 'info', c: 'If you ever suspect a password or an API Key was compromised, you can change or revoke it instantly from Settings or the Developer panel, without having to write to us first.' },
    ],
  },
  {
    slug: 'brand-kit',
    category: 'cuenta',
    title: 'Brand Kit: your company’s visual identity',
    excerpt: 'One template and one color palette for the whole team.',
    blocks: [
      { t: 'p', c: 'Brand Kit lets you define a template and a corporate color palette once, and have them inherited automatically by every team member’s profile: that way every card from your company looks consistent, without relying on each person configuring their own appearance correctly.' },
      { t: 'p', c: 'While Brand Kit is active, individual template and color customization on each member’s profile is locked, precisely to protect that brand consistency.' },
    ],
  },
  {
    slug: 'soporte',
    category: 'cuenta',
    title: 'Support: how to reach us',
    excerpt: 'Who to write to depending on your question.',
    blocks: [
      { t: 'p', c: 'If something isn’t working as expected or you have a specific question about your account, the most direct channel is the Support tab inside Settings, in your dashboard: it reaches our team directly.' },
      { t: 'p', c: 'If you don’t have an account yet and just want a quick question answered before buying (pricing, shipping, compatibility), you can write to our assistant on the public site: you’ll find it as a chat bubble in the bottom corner of the page.' },
    ],
  },
];

const ARTICLES_CUENTA_PT = [
  {
    slug: 'planes',
    category: 'cuenta',
    title: 'Planos disponíveis: Gratuito, Pro e Enterprise',
    excerpt: 'Tabela comparativa completa.',
    popular: true,
    blocks: [
      { t: 'table', headers: ['', 'Gratuito', 'Pro', 'Enterprise'], rows: [
        ['Perfis', '1', '3', '10'],
        ['Leads', 'Até 5 por mês', 'Ilimitados', 'Ilimitados'],
        ['Analytics', '4 KPIs básicos', 'Completo + Insights com IA', 'Completo + Analytics de Equipe'],
        ['Imagem de fundo personalizada', 'Não', 'Sim', 'Sim'],
        ['Remover marca d’água', 'Não', 'Sim', 'Sim'],
        ['Hub (CRM)', 'Não', 'Sim', 'Sim'],
        ['Campanhas e Eventos', 'Não', 'Sim', 'Sim'],
        ['Follow-ups automáticos', 'Não', 'Sim', 'Sim'],
        ['Exportar relatórios', 'Não', 'Sim', 'Sim'],
        ['Cotações', 'Não', 'Sim', 'Sim'],
        ['Google Sheets', 'Não', 'Sim', 'Sim'],
        ['API pública e Webhooks', 'Não', 'Não', 'Sim'],
        ['Equipes e chaves de acesso', 'Não', 'Não', 'Sim'],
        ['Analytics de Equipe', 'Não', 'Não', 'Sim'],
        ['Brand Kit', 'Não', 'Não', 'Sim'],
      ] },
      { t: 'p', c: 'O plano Pro custa $2.500 CLP por mês, ou $25.000 CLP por ano (equivalente a 2 meses grátis se você pagar anual). O Enterprise não tem um preço fixo publicado: é cotado de acordo com o tamanho da sua equipe.' },
    ],
  },
  {
    slug: 'gestionar-suscripcion',
    category: 'cuenta',
    title: 'Gerencie sua assinatura',
    excerpt: 'Trocar de plano, pausar, cancelar.',
    blocks: [
      { t: 'p', c: 'Em Configurações → Plano você pode fazer upgrade do Gratuito para o Pro, trocar entre pagamento mensal e anual, pausar sua assinatura (ela permanece válida, porém sem cobrança até que você a reative) ou cancelá-la. Se você cancelar, mantém o acesso aos recursos do seu plano até o fim do período já pago: você não perde o que já pagou no meio do caminho.' },
      { t: 'p', c: 'A cobrança recorrente é processada pelo MercadoPago. Se por algum motivo uma cobrança não for processada corretamente, sua conta volta automaticamente para o Gratuito até que isso seja regularizado.' },
    ],
  },
  {
    slug: 'configuracion-cuenta',
    category: 'cuenta',
    title: 'Configurações da sua conta',
    excerpt: 'Idioma, senha e exclusão da sua conta.',
    blocks: [
      { t: 'p', c: 'Em Configurações → Conta você pode trocar o idioma do painel, seu nome e sua senha.' },
      { t: 'callout', variant: 'warn', c: 'Na "Zona de perigo" das Configurações você pode excluir sua conta por completo. Essa ação apaga seu perfil, seus Leads, seus Analytics e qualquer dado associado, e não pode ser desfeita.' },
    ],
  },
  {
    slug: 'equipos',
    category: 'cuenta',
    title: 'Equipes: perfis e acessos',
    excerpt: 'Uma conta, vários perfis, cada um com seu próprio acesso.',
    blocks: [
      { t: 'p', c: 'Equipes foi pensado para empresas que querem dar um cartão Contact a várias pessoas sob uma única conta administrada centralmente. No painel de Equipes você define os dados da empresa (segmento, descrição) e administra cada membro.' },
      { t: 'p', c: 'Cada membro da equipe recebe sua própria chave de acesso, um login independente ligado especificamente ao seu perfil: assim cada pessoa pode entrar e atualizar suas próprias informações sem ver ou tocar nas dos colegas, enquanto você mantém a administração geral pela conta principal.' },
    ],
  },
  {
    slug: 'seguridad-privacidad',
    category: 'cuenta',
    title: 'Segurança e privacidade dos seus dados',
    excerpt: 'Como protegemos sua conta e as informações dos seus contatos.',
    blocks: [
      { t: 'p', c: 'Sua senha nunca é armazenada em texto simples: ela é guardada com um hash criptográfico que torna impossível recuperá-la no texto original, nem mesmo para nossa própria equipe. Fazer login gera um token de sessão (JWT) que viaja de forma segura entre seu navegador e nossos servidores.' },
      { t: 'p', c: 'Se você conectar o Google Sheets, o Contact só pode criar e escrever em uma planilha nova gerada especificamente para esse fim: nunca acessa outras planilhas ou arquivos existentes no seu Drive.' },
      { t: 'p', c: 'Se você usar Webhooks (Enterprise), cada entrega é assinada com uma chave única por webhook (HMAC) para que seu sistema possa verificar que o envio realmente veio do Contact, e exigimos que a URL de destino seja HTTPS: uma URL sem criptografia ou que aponte para uma rede interna nunca é permitida.' },
      { t: 'callout', variant: 'info', c: 'Se alguma vez você suspeitar que uma senha ou uma API Key foi comprometida, pode trocá-la ou revogá-la instantaneamente em Configurações ou no painel do Desenvolvedor, sem precisar nos escrever primeiro.' },
    ],
  },
  {
    slug: 'brand-kit',
    category: 'cuenta',
    title: 'Brand Kit: identidade visual da sua empresa',
    excerpt: 'Um modelo e cores únicos para toda a equipe.',
    blocks: [
      { t: 'p', c: 'O Brand Kit permite definir um modelo e uma paleta de cores corporativas uma única vez, e faz com que sejam herdados automaticamente pelos perfis de todos os membros da sua equipe: assim todos os cartões da sua empresa ficam consistentes entre si, sem depender de cada pessoa configurar bem a própria aparência.' },
      { t: 'p', c: 'Quando o Brand Kit está ativo, a personalização individual de modelo e cor no perfil de cada membro fica bloqueada, justamente para proteger essa consistência de marca.' },
    ],
  },
  {
    slug: 'soporte',
    category: 'cuenta',
    title: 'Suporte: como entrar em contato',
    excerpt: 'Para quem escrever de acordo com sua dúvida.',
    blocks: [
      { t: 'p', c: 'Se algo não está funcionando como esperado ou você tem uma dúvida específica sobre sua conta, o canal mais direto é a aba Suporte dentro de Configurações, no seu painel: dali chega direto à nossa equipe.' },
      { t: 'p', c: 'Se você ainda não tem conta e só quer resolver uma pergunta rápida antes de comprar (preços, envios, compatibilidade), pode escrever para nosso assistente no site público: você vai encontrá-lo como um balão de chat no canto inferior da página.' },
    ],
  },
];

// ═══════════════════════════ DEVELOPERS ═══════════════════════════

const ARTICLES_DEVELOPERS_ES = [
  {
    slug: 'integraciones-disponibles',
    category: 'developers',
    title: 'Integraciones disponibles',
    excerpt: 'Qué puedes conectar hoy, y qué viene en camino.',
    blocks: [
      { t: 'p', c: 'Hoy, la integración activa es Google Sheets: cada Lead nuevo se agrega automáticamente a una planilla propia, sin que tengas que exportar ni copiar nada a mano.' },
      { t: 'p', c: 'Si tienes un plan Enterprise y necesitas algo más a medida, los Webhooks te dejan conectar Contact con prácticamente cualquier sistema propio (ver la sección de Webhooks más abajo): es la vía más flexible mientras el catálogo de integraciones nativas sigue creciendo.' },
      { t: 'p', c: 'Estamos trabajando en sumar más integraciones (herramientas de CRM, calendario y correo): si hay una herramienta puntual que necesitas conectar, cuéntanos desde Soporte.' },
    ],
  },
  {
    slug: 'google-sheets',
    category: 'developers',
    title: 'Conecta Google Sheets',
    excerpt: 'Tus Leads, siempre sincronizados en una planilla.',
    blocks: [
      { t: 'p', c: 'Desde Integraciones, conecta tu cuenta de Google con un par de clics. Contact crea una planilla nueva exclusivamente para esto: nunca accede a otras planillas ni a archivos existentes en tu Google Drive.' },
      { t: 'steps', items: [
        'Entra a Integraciones y elige Google Sheets.',
        'Autoriza el acceso con tu cuenta de Google.',
        'La planilla se crea automáticamente.',
        'Cada Lead nuevo se agrega a la planilla sin que tengas que hacer nada más.',
      ] },
      { t: 'p', c: 'Puedes pausar la integración, probarla, o desconectarla por completo en cualquier momento desde el mismo panel.' },
    ],
  },
  {
    slug: 'api-publica',
    category: 'developers',
    title: 'API pública de Contact',
    excerpt: 'Endpoints disponibles y cómo autenticarte.',
    blocks: [
      { t: 'p', c: 'La API pública te deja leer tus Leads y un resumen de tus Analíticas desde tu propio sistema, autenticándote con una API Key que generas tú mismo desde el panel de Desarrollador.' },
      { t: 'h', c: 'Endpoints' },
      { t: 'table', headers: ['Endpoint', 'Qué hace', 'Límite'], rows: [
        ['GET /v1/leads', 'Lista tus Leads, paginada y filtrable por perfil', '120 solicitudes/min'],
        ['GET /v1/analytics/summary', 'Resumen de visitas, clics y leads por perfil', '30 solicitudes/min'],
        ['GET /v1/ping', 'Chequeo de salud de la conexión', '60 solicitudes/min'],
      ] },
      { t: 'callout', variant: 'info', c: 'Autentícate enviando tu API Key como bearer token en el header Authorization: Bearer cnk_live_... Las API Keys con prefijo cnk_test_ operan en modo sandbox, con datos de prueba, para que puedas integrar sin tocar información real.' },
    ],
  },
  {
    slug: 'api-keys',
    category: 'developers',
    title: 'Crea y administra tus API Keys',
    excerpt: 'Generación, revocación y expiración.',
    blocks: [
      { t: 'p', c: 'Desde el panel de Desarrollador puedes generar una nueva API Key en cualquier momento. La clave completa se muestra una sola vez al crearla: guárdala de inmediato, porque después solo vas a ver una versión parcial por seguridad.' },
      { t: 'list', items: [
        'Puedes definir si la clave expira en 30, 60 o 90 días, o si no expira nunca.',
        'Puedes revocar una clave al instante si sospechas que se filtró, sin afectar a tus demás claves activas.',
        'El panel de Logs te muestra la actividad reciente de cada clave contra la API.',
      ] },
      { t: 'p', c: 'También puedes probar los endpoints directamente desde el panel antes de integrarlos en tu propio sistema, y descargar una colección de Postman ya configurada con todos los endpoints disponibles.' },
    ],
  },
  {
    slug: 'webhooks',
    category: 'developers',
    title: 'Webhooks: recibe eventos en tiempo real',
    excerpt: 'lead.created, firmado con HMAC.',
    blocks: [
      { t: 'p', c: 'En vez de consultar la API a cada rato para ver si hay Leads nuevos, puedes registrar un webhook: una URL tuya a la que Contact le avisa automáticamente apenas ocurre un evento.' },
      { t: 'h', c: 'Evento disponible' },
      { t: 'p', c: 'Hoy el único evento soportado es lead.created, que se dispara cada vez que entra un Lead nuevo a cualquiera de tus perfiles.' },
      { t: 'h', c: 'Requisitos de la URL' },
      { t: 'list', items: [
        'Debe ser HTTPS (no se admite http:// ni localhost).',
        'No puede apuntar a una IP privada o reservada, por seguridad.',
      ] },
      { t: 'h', c: 'Verifica la firma' },
      { t: 'p', c: 'Cada envío viene firmado con HMAC usando un secreto único de tu webhook, para que puedas confirmar que el request realmente viene de Contact y no de un tercero. Puedes rotar ese secreto cuando quieras desde el panel.' },
      { t: 'p', c: 'Si uno de tus envíos falla, puedes ver el historial de entregas y reintentar manualmente las que no llegaron, o usar el botón de "enviar una prueba" para confirmar que tu endpoint está respondiendo bien antes de depender de él en producción.' },
    ],
  },
  {
    slug: 'postman',
    category: 'developers',
    title: 'Colección Postman',
    excerpt: 'Todos los endpoints listos para importar.',
    blocks: [
      { t: 'p', c: 'Desde el panel de Desarrollador puedes descargar una colección de Postman (formato v2.1) con todos los endpoints de la API pública ya armados, para que puedas empezar a probar llamadas en segundos sin escribir cada request a mano.' },
    ],
  },
  {
    slug: 'errores-api',
    category: 'developers',
    title: 'Errores y códigos de respuesta',
    excerpt: 'Qué significa cada código al llamar la API.',
    blocks: [
      { t: 'p', c: 'La API pública responde con códigos HTTP estándar, para que puedas manejar cada caso de forma predecible desde tu propio sistema.' },
      { t: 'table', headers: ['Código', 'Significado', 'Qué revisar'], rows: [
        ['200', 'OK', 'La solicitud se procesó correctamente.'],
        ['401', 'No autorizado', 'Tu API Key falta, es inválida, o fue revocada.'],
        ['404', 'No encontrado', 'El recurso que pediste (por ejemplo, un perfil) no existe o no te pertenece.'],
        ['429', 'Demasiadas solicitudes', 'Superaste el límite de ese endpoint: revisa la tabla de límites en API pública de Contact.'],
        ['500', 'Error del servidor', 'Algo falló de nuestro lado. Si se repite, escríbenos desde Soporte con el detalle.'],
      ] },
      { t: 'callout', variant: 'info', c: 'Las API Keys con prefijo cnk_test_ nunca van a afectar datos reales, así que son el lugar seguro para probar cómo tu sistema maneja cada uno de estos códigos antes de integrarte con tu API Key de producción (cnk_live_).' },
    ],
  },
];

const ARTICLES_DEVELOPERS_EN = [
  {
    slug: 'integraciones-disponibles',
    category: 'developers',
    title: 'Available integrations',
    excerpt: 'What you can connect today, and what’s coming.',
    blocks: [
      { t: 'p', c: 'Today, the active integration is Google Sheets: every new Lead is automatically added to a spreadsheet of its own, with nothing for you to export or copy by hand.' },
      { t: 'p', c: 'If you have an Enterprise plan and need something more custom, Webhooks let you connect Contact to practically any system of your own (see the Webhooks section below): it’s the most flexible path while the catalog of native integrations keeps growing.' },
      { t: 'p', c: 'We’re working on adding more integrations (CRM, calendar and email tools): if there’s a specific tool you need connected, tell us from Support.' },
    ],
  },
  {
    slug: 'google-sheets',
    category: 'developers',
    title: 'Connect Google Sheets',
    excerpt: 'Your Leads, always synced to a spreadsheet.',
    blocks: [
      { t: 'p', c: 'From Integrations, connect your Google account in a couple of clicks. Contact creates a new spreadsheet exclusively for this: it never accesses other spreadsheets or existing files in your Google Drive.' },
      { t: 'steps', items: [
        'Go to Integrations and choose Google Sheets.',
        'Authorize access with your Google account.',
        'The spreadsheet is created automatically.',
        'Every new Lead gets added to the spreadsheet with nothing else for you to do.',
      ] },
      { t: 'p', c: 'You can pause the integration, test it, or disconnect it entirely at any time from the same panel.' },
    ],
  },
  {
    slug: 'api-publica',
    category: 'developers',
    title: 'Contact’s public API',
    excerpt: 'Available endpoints and how to authenticate.',
    blocks: [
      { t: 'p', c: 'The public API lets you read your Leads and a summary of your Analytics from your own system, authenticating with an API Key you generate yourself from the Developer panel.' },
      { t: 'h', c: 'Endpoints' },
      { t: 'table', headers: ['Endpoint', 'What it does', 'Limit'], rows: [
        ['GET /v1/leads', 'Lists your Leads, paginated and filterable by profile', '120 requests/min'],
        ['GET /v1/analytics/summary', 'Summary of visits, clicks and leads per profile', '30 requests/min'],
        ['GET /v1/ping', 'Connection health check', '60 requests/min'],
      ] },
      { t: 'callout', variant: 'info', c: 'Authenticate by sending your API Key as a bearer token in the Authorization header: Bearer cnk_live_... API Keys with the cnk_test_ prefix operate in sandbox mode, with test data, so you can integrate without touching real information.' },
    ],
  },
  {
    slug: 'api-keys',
    category: 'developers',
    title: 'Create and manage your API Keys',
    excerpt: 'Generation, revocation and expiration.',
    blocks: [
      { t: 'p', c: 'From the Developer panel you can generate a new API Key at any time. The full key is shown only once, when you create it: save it right away, because afterward you’ll only see a partial version for security.' },
      { t: 'list', items: [
        'You can set the key to expire in 30, 60 or 90 days, or to never expire.',
        'You can revoke a key instantly if you suspect it leaked, without affecting your other active keys.',
        'The Logs panel shows each key’s recent activity against the API.',
      ] },
      { t: 'p', c: 'You can also test the endpoints directly from the panel before integrating them into your own system, and download a Postman collection already set up with every available endpoint.' },
    ],
  },
  {
    slug: 'webhooks',
    category: 'developers',
    title: 'Webhooks: get events in real time',
    excerpt: 'lead.created, signed with HMAC.',
    blocks: [
      { t: 'p', c: 'Instead of polling the API over and over to check for new Leads, you can register a webhook: a URL of yours that Contact notifies automatically the moment an event happens.' },
      { t: 'h', c: 'Available event' },
      { t: 'p', c: 'Today the only supported event is lead.created, which fires every time a new Lead comes in through any of your profiles.' },
      { t: 'h', c: 'URL requirements' },
      { t: 'list', items: [
        'Must be HTTPS (no http:// or localhost allowed).',
        'Can’t point to a private or reserved IP, for security.',
      ] },
      { t: 'h', c: 'Verify the signature' },
      { t: 'p', c: 'Every delivery is signed with HMAC using a secret unique to your webhook, so you can confirm the request really came from Contact and not a third party. You can rotate that secret whenever you want from the panel.' },
      { t: 'p', c: 'If one of your deliveries fails, you can see the delivery history and manually retry the ones that didn’t arrive, or use the "send a test" button to confirm your endpoint is responding correctly before relying on it in production.' },
    ],
  },
  {
    slug: 'postman',
    category: 'developers',
    title: 'Postman collection',
    excerpt: 'Every endpoint, ready to import.',
    blocks: [
      { t: 'p', c: 'From the Developer panel you can download a Postman collection (v2.1 format) with every public API endpoint already set up, so you can start testing calls in seconds without typing each request by hand.' },
    ],
  },
  {
    slug: 'errores-api',
    category: 'developers',
    title: 'Errors and response codes',
    excerpt: 'What each code means when calling the API.',
    blocks: [
      { t: 'p', c: 'The public API responds with standard HTTP codes, so you can handle every case predictably from your own system.' },
      { t: 'table', headers: ['Code', 'Meaning', 'What to check'], rows: [
        ['200', 'OK', 'The request was processed successfully.'],
        ['401', 'Unauthorized', 'Your API Key is missing, invalid, or was revoked.'],
        ['404', 'Not found', 'The resource you requested (for example, a profile) doesn’t exist or doesn’t belong to you.'],
        ['429', 'Too many requests', 'You went over that endpoint’s limit: check the limits table in Contact’s public API.'],
        ['500', 'Server error', 'Something failed on our end. If it keeps happening, write to us from Support with the details.'],
      ] },
      { t: 'callout', variant: 'info', c: 'API Keys with the cnk_test_ prefix will never affect real data, so they’re the safe place to test how your system handles each of these codes before integrating with your production API Key (cnk_live_).' },
    ],
  },
];

const ARTICLES_DEVELOPERS_PT = [
  {
    slug: 'integraciones-disponibles',
    category: 'developers',
    title: 'Integrações disponíveis',
    excerpt: 'O que você pode conectar hoje, e o que está por vir.',
    blocks: [
      { t: 'p', c: 'Hoje, a integração ativa é o Google Sheets: cada novo Lead é adicionado automaticamente a uma planilha própria, sem que você precise exportar ou copiar nada manualmente.' },
      { t: 'p', c: 'Se você tem um plano Enterprise e precisa de algo mais personalizado, os Webhooks permitem conectar o Contact com praticamente qualquer sistema seu (veja a seção de Webhooks abaixo): é o caminho mais flexível enquanto o catálogo de integrações nativas continua crescendo.' },
      { t: 'p', c: 'Estamos trabalhando para adicionar mais integrações (ferramentas de CRM, calendário e email): se há uma ferramenta específica que você precisa conectar, conte para nós pelo Suporte.' },
    ],
  },
  {
    slug: 'google-sheets',
    category: 'developers',
    title: 'Conecte o Google Sheets',
    excerpt: 'Seus Leads, sempre sincronizados em uma planilha.',
    blocks: [
      { t: 'p', c: 'Em Integrações, conecte sua conta do Google em poucos cliques. O Contact cria uma planilha nova exclusivamente para isso: nunca acessa outras planilhas ou arquivos existentes no seu Google Drive.' },
      { t: 'steps', items: [
        'Entre em Integrações e escolha Google Sheets.',
        'Autorize o acesso com sua conta do Google.',
        'A planilha é criada automaticamente.',
        'Cada novo Lead é adicionado à planilha sem que você precise fazer mais nada.',
      ] },
      { t: 'p', c: 'Você pode pausar a integração, testá-la, ou desconectá-la por completo a qualquer momento pelo mesmo painel.' },
    ],
  },
  {
    slug: 'api-publica',
    category: 'developers',
    title: 'API pública do Contact',
    excerpt: 'Endpoints disponíveis e como se autenticar.',
    blocks: [
      { t: 'p', c: 'A API pública permite ler seus Leads e um resumo dos seus Analytics a partir do seu próprio sistema, autenticando com uma API Key que você mesmo gera no painel do Desenvolvedor.' },
      { t: 'h', c: 'Endpoints' },
      { t: 'table', headers: ['Endpoint', 'O que faz', 'Limite'], rows: [
        ['GET /v1/leads', 'Lista seus Leads, paginada e filtrável por perfil', '120 solicitações/min'],
        ['GET /v1/analytics/summary', 'Resumo de visitas, cliques e leads por perfil', '30 solicitações/min'],
        ['GET /v1/ping', 'Verificação de saúde da conexão', '60 solicitações/min'],
      ] },
      { t: 'callout', variant: 'info', c: 'Autentique-se enviando sua API Key como bearer token no cabeçalho Authorization: Bearer cnk_live_... As API Keys com prefixo cnk_test_ operam em modo sandbox, com dados de teste, para que você possa integrar sem tocar em informações reais.' },
    ],
  },
  {
    slug: 'api-keys',
    category: 'developers',
    title: 'Crie e administre suas API Keys',
    excerpt: 'Geração, revogação e expiração.',
    blocks: [
      { t: 'p', c: 'No painel do Desenvolvedor você pode gerar uma nova API Key a qualquer momento. A chave completa é mostrada uma única vez ao criá-la: salve-a imediatamente, porque depois você só vai ver uma versão parcial por segurança.' },
      { t: 'list', items: [
        'Você pode definir se a chave expira em 30, 60 ou 90 dias, ou se nunca expira.',
        'Você pode revogar uma chave instantaneamente se suspeitar que ela foi exposta, sem afetar suas outras chaves ativas.',
        'O painel de Logs mostra a atividade recente de cada chave na API.',
      ] },
      { t: 'p', c: 'Você também pode testar os endpoints diretamente no painel antes de integrá-los ao seu próprio sistema, e baixar uma coleção do Postman já configurada com todos os endpoints disponíveis.' },
    ],
  },
  {
    slug: 'webhooks',
    category: 'developers',
    title: 'Webhooks: receba eventos em tempo real',
    excerpt: 'lead.created, assinado com HMAC.',
    blocks: [
      { t: 'p', c: 'Em vez de consultar a API o tempo todo para ver se há Leads novos, você pode registrar um webhook: uma URL sua para a qual o Contact avisa automaticamente assim que um evento ocorre.' },
      { t: 'h', c: 'Evento disponível' },
      { t: 'p', c: 'Hoje o único evento suportado é lead.created, que é disparado cada vez que um novo Lead entra em qualquer um dos seus perfis.' },
      { t: 'h', c: 'Requisitos da URL' },
      { t: 'list', items: [
        'Deve ser HTTPS (não é permitido http:// nem localhost).',
        'Não pode apontar para um IP privado ou reservado, por segurança.',
      ] },
      { t: 'h', c: 'Verifique a assinatura' },
      { t: 'p', c: 'Cada envio vem assinado com HMAC usando um segredo único do seu webhook, para que você possa confirmar que a solicitação realmente veio do Contact e não de terceiros. Você pode rotacionar esse segredo quando quiser pelo painel.' },
      { t: 'p', c: 'Se um dos seus envios falhar, você pode ver o histórico de entregas e tentar novamente manualmente as que não chegaram, ou usar o botão de "enviar um teste" para confirmar que seu endpoint está respondendo bem antes de depender dele em produção.' },
    ],
  },
  {
    slug: 'postman',
    category: 'developers',
    title: 'Coleção Postman',
    excerpt: 'Todos os endpoints prontos para importar.',
    blocks: [
      { t: 'p', c: 'No painel do Desenvolvedor você pode baixar uma coleção do Postman (formato v2.1) com todos os endpoints da API pública já montados, para que você possa comear a testar chamadas em segundos sem escrever cada requisição manualmente.' },
    ],
  },
  {
    slug: 'errores-api',
    category: 'developers',
    title: 'Erros e códigos de resposta',
    excerpt: 'O que cada código significa ao chamar a API.',
    blocks: [
      { t: 'p', c: 'A API pública responde com códigos HTTP padrão, para que você possa tratar cada caso de forma previsível a partir do seu próprio sistema.' },
      { t: 'table', headers: ['Código', 'Significado', 'O que verificar'], rows: [
        ['200', 'OK', 'A solicitação foi processada corretamente.'],
        ['401', 'Não autorizado', 'Sua API Key está faltando, é inválida, ou foi revogada.'],
        ['404', 'Não encontrado', 'O recurso que você pediu (por exemplo, um perfil) não existe ou não pertence a você.'],
        ['429', 'Muitas solicitações', 'Você excedeu o limite desse endpoint: veja a tabela de limites em API pública do Contact.'],
        ['500', 'Erro do servidor', 'Algo falhou do nosso lado. Se persistir, escreva para nós pelo Suporte com os detalhes.'],
      ] },
      { t: 'callout', variant: 'info', c: 'API Keys com prefixo cnk_test_ nunca vão afetar dados reais, entao são o lugar seguro para testar como seu sistema lida com cada um desses códigos antes de integrar com sua API Key de produção (cnk_live_).' },
    ],
  },
];

const ARTICLES_ES = [...ARTICLES_EMPEZANDO_ES, ...ARTICLES_PERFIL_ES, ...ARTICLES_GUIAS_ES, ...ARTICLES_LEADS_ES, ...ARTICLES_ANALITICA_ES, ...ARTICLES_PROBLEMAS_ES, ...ARTICLES_CUENTA_ES, ...ARTICLES_DEVELOPERS_ES];
const ARTICLES_EN = [...ARTICLES_EMPEZANDO_EN, ...ARTICLES_PERFIL_EN, ...ARTICLES_GUIAS_EN, ...ARTICLES_LEADS_EN, ...ARTICLES_ANALITICA_EN, ...ARTICLES_PROBLEMAS_EN, ...ARTICLES_CUENTA_EN, ...ARTICLES_DEVELOPERS_EN];
const ARTICLES_PT = [...ARTICLES_EMPEZANDO_PT, ...ARTICLES_PERFIL_PT, ...ARTICLES_GUIAS_PT, ...ARTICLES_LEADS_PT, ...ARTICLES_ANALITICA_PT, ...ARTICLES_PROBLEMAS_PT, ...ARTICLES_CUENTA_PT, ...ARTICLES_DEVELOPERS_PT];

export const DOCS_ARTICLES_I18N = { es: ARTICLES_ES, en: ARTICLES_EN, pt: ARTICLES_PT };

/* Short FAQ per category: rendered as the last section of each
   category page. Kept to things confirmed by the product, not
   invented policy. */
const FAQS_ES = {
  empezando: [
    { q: '¿Necesito instalar una app para usar mi tarjeta?', a: 'No. NFC y QR abren tu perfil directo en el navegador del celular de la otra persona, tanto en iPhone como en Android.' },
    { q: '¿Puedo comprar tarjetas para todo mi equipo de una vez?', a: 'Sí, puedes elegir la cantidad que necesites. El envío es gratis desde 2 unidades.' },
    { q: '¿Qué pasa si me equivoco en el diseño de la tarjeta?', a: 'Si detectas un error antes de que tu pedido pase a producción, contáctanos desde Soporte lo antes posible para corregirlo.' },
  ],
  perfil: [
    { q: '¿Puedo cambiar mi perfil después de recibir la tarjeta física?', a: 'Sí. Tu perfil vive en tu cuenta, no en la tarjeta: cualquier cambio se refleja al instante sin reimprimir nada.' },
    { q: '¿Puedo usar plantillas distintas en cada perfil de mi cuenta?', a: 'Sí, cada perfil elige su plantilla de forma independiente, salvo que tu cuenta tenga Brand Kit activo.' },
    { q: '¿Qué pasa si mi archivo adjunto pesa más de 3 MB?', a: 'Tendrás que comprimirlo antes de subirlo: ese es el tope por archivo.' },
  ],
  leads: [
    { q: '¿Un contacto agregado a mano en el Hub recibe Follow-ups?', a: 'No. Los Follow-ups automáticos solo se activan cuando el contacto entra por un toque o escaneo real de tu tarjeta.' },
    { q: '¿Puedo editar los correos de la secuencia de Follow-ups?', a: 'Sí, las 3 plantillas son completamente editables, y puedes mandarte un correo de prueba antes de activarla.' },
    { q: '¿Cómo sé qué evento me generó más Leads?', a: 'Si agrupaste tus perfiles en una Campaña, el panel de Campañas te muestra el desglose completo por evento.' },
  ],
  analitica: [
    { q: '¿Qué pasa con mis Analíticas si bajo a Free?', a: 'Tu cuenta vuelve a mostrar los 4 KPIs básicos; las funciones avanzadas (tendencia, Insights, exportación) quedan bloqueadas hasta que vuelvas a un plan pago.' },
    { q: '¿Qué cuenta como una "interacción"?', a: 'Cualquier clic en tus redes, descarga de tu contacto, o descarga de tu pase de Apple Wallet.' },
    { q: '¿Puedo ver analíticas de un perfil específico si tengo varios?', a: 'Sí, cada perfil tiene sus propias métricas; si administras un equipo Enterprise, además puedes ver el agregado de todos en Analítica de Equipo.' },
  ],
  problemas: [
    { q: '¿Por qué mi tarjeta funcionaba antes y ahora no?', a: 'Lo más común es que el NFC del celular que la acerca se haya desactivado, no un problema de la tarjeta: confirma eso primero, y si no, prueba con el código QR.' },
    { q: '¿Es normal que no lleguen los correos 2 y 3 de Follow-ups?', a: 'Sí, si la detección de respuestas no está configurada en tu cuenta, esos dos correos se omiten a propósito: el primero sí se envía siempre.' },
    { q: '¿Cuánto dura el bloqueo por intentos fallidos al iniciar sesión?', a: 'Unos minutos. Pasado ese tiempo puedes volver a intentar con tu contraseña correcta.' },
  ],
  cuenta: [
    { q: '¿Puedo cambiar de plan cuando quiera?', a: 'Sí, puedes subir o bajar de plan desde Configuración → Plan en cualquier momento.' },
    { q: '¿Qué pasa si cancelo mi suscripción a mitad de mes?', a: 'Mantienes el acceso a las funciones de tu plan hasta el final del periodo que ya pagaste.' },
    { q: '¿Puedo recuperar mi cuenta después de eliminarla?', a: 'No, eliminar tu cuenta borra tu perfil, tus Leads y tus Analíticas de forma permanente: no se puede deshacer.' },
  ],
  developers: [
    { q: '¿La API pública tiene un costo aparte?', a: 'No, está incluida dentro del plan Enterprise.' },
    { q: '¿Puedo probar la API antes de usarla en producción?', a: 'Sí, generando una API Key en modo sandbox (prefijo cnk_test_), que trabaja con datos de prueba.' },
    { q: '¿Qué pasa si una entrega de webhook falla?', a: 'Queda registrada en el historial de entregas de ese webhook, y puedes reintentarla manualmente cuando quieras.' },
  ],
};

const FAQS_EN = {
  empezando: [
    { q: 'Do I need to install an app to use my card?', a: 'No. NFC and QR both open your profile straight in the other person’s phone browser, on iPhone and Android alike.' },
    { q: 'Can I buy cards for my whole team at once?', a: 'Yes, you can choose whatever quantity you need. Shipping is free from 2 units up.' },
    { q: 'What if I make a mistake in the card design?', a: 'If you catch an error before your order moves to production, contact us from Support as soon as possible to fix it.' },
  ],
  perfil: [
    { q: 'Can I change my profile after receiving the physical card?', a: 'Yes. Your profile lives in your account, not on the card: any change shows up instantly, nothing to reprint.' },
    { q: 'Can I use different templates on each profile in my account?', a: 'Yes, each profile picks its template independently, unless your account has Brand Kit active.' },
    { q: 'What if my attached file is heavier than 3 MB?', a: 'You’ll need to compress it before uploading: that’s the per-file cap.' },
  ],
  leads: [
    { q: 'Does a contact added by hand in the Hub get Follow-ups?', a: 'No. Automatic Follow-ups only activate when the contact comes in through a real tap or scan of your card.' },
    { q: 'Can I edit the emails in the Follow-up sequence?', a: 'Yes, all 3 templates are fully editable, and you can send yourself a test email before turning it on.' },
    { q: 'How do I know which event generated the most Leads for me?', a: 'If you grouped your profiles into a Campaign, the Campaigns panel shows you the full breakdown by event.' },
  ],
  analitica: [
    { q: 'What happens to my Analytics if I downgrade to Free?', a: 'Your account goes back to showing the 4 basic KPIs; the advanced features (trend, Insights, export) stay locked until you go back to a paid plan.' },
    { q: 'What counts as an "interaction"?', a: 'Any click on your networks, a contact download, or an Apple Wallet pass download.' },
    { q: 'Can I see analytics for a specific profile if I have several?', a: 'Yes, each profile has its own metrics; if you manage an Enterprise team, you can also see everyone’s combined totals in Team Analytics.' },
  ],
  problemas: [
    { q: 'Why did my card used to work and now it doesn’t?', a: 'The most common cause is that NFC got turned off on the phone tapping it, not an issue with the card: check that first, and if not, try the QR code.' },
    { q: 'Is it normal for emails 2 and 3 of Follow-ups to not arrive?', a: 'Yes, if reply detection isn’t configured on your account, those two emails are skipped on purpose: the first one always sends.' },
    { q: 'How long does the lockout from failed login attempts last?', a: 'A few minutes. After that you can try again with your correct password.' },
  ],
  cuenta: [
    { q: 'Can I change plans whenever I want?', a: 'Yes, you can upgrade or downgrade from Settings → Plan at any time.' },
    { q: 'What happens if I cancel my subscription mid-month?', a: 'You keep access to your plan’s features until the end of the period you already paid for.' },
    { q: 'Can I recover my account after deleting it?', a: 'No, deleting your account permanently erases your profile, your Leads and your Analytics: it can’t be undone.' },
  ],
  developers: [
    { q: 'Does the public API cost extra?', a: 'No, it’s included with the Enterprise plan.' },
    { q: 'Can I test the API before using it in production?', a: 'Yes, by generating an API Key in sandbox mode (cnk_test_ prefix), which works with test data.' },
    { q: 'What happens if a webhook delivery fails?', a: 'It gets logged in that webhook’s delivery history, and you can manually retry it whenever you want.' },
  ],
};

const FAQS_PT = {
  empezando: [
    { q: 'Preciso instalar um app para usar meu cartão?', a: 'Não. NFC e QR abrem seu perfil direto no navegador do celular da outra pessoa, tanto em iPhone quanto em Android.' },
    { q: 'Posso comprar cartões para toda a minha equipe de uma vez?', a: 'Sim, você pode escolher a quantidade que precisar. O envio é grátis a partir de 2 unidades.' },
    { q: 'O que acontece se eu errar no design do cartão?', a: 'Se você detectar um erro antes do seu pedido entrar em produção, entre em contato pelo Suporte o quanto antes para corrigi-lo.' },
  ],
  perfil: [
    { q: 'Posso mudar meu perfil depois de receber o cartão físico?', a: 'Sim. Seu perfil vive na sua conta, não no cartão: qualquer mudança aparece instantaneamente, sem reimprimir nada.' },
    { q: 'Posso usar modelos diferentes em cada perfil da minha conta?', a: 'Sim, cada perfil escolhe seu modelo de forma independente, a menos que sua conta tenha o Brand Kit ativo.' },
    { q: 'O que acontece se meu arquivo anexado pesar mais de 3 MB?', a: 'Você vai precisar comprimi-lo antes de enviar: esse é o limite por arquivo.' },
  ],
  leads: [
    { q: 'Um contato adicionado manualmente no Hub recebe Follow-ups?', a: 'Não. Os Follow-ups automáticos só são ativados quando o contato entra por um toque ou escaneamento real do seu cartão.' },
    { q: 'Posso editar os emails da sequência de Follow-ups?', a: 'Sim, os 3 modelos são completamente editáveis, e você pode enviar um email de teste para você mesmo antes de ativá-la.' },
    { q: 'Como sei qual evento gerou mais Leads para mim?', a: 'Se você agrupou seus perfis em uma Campanha, o painel de Campanhas mostra o detalhamento completo por evento.' },
  ],
  analitica: [
    { q: 'O que acontece com meus Analytics se eu voltar para o Gratuito?', a: 'Sua conta volta a mostrar os 4 KPIs básicos; os recursos avançados (tendência, Insights, exportação) ficam bloqueados até você voltar a um plano pago.' },
    { q: 'O que conta como uma "interação"?', a: 'Qualquer clique nas suas redes, download do seu contato, ou download do seu passe do Apple Wallet.' },
    { q: 'Posso ver analytics de um perfil específico se eu tiver vários?', a: 'Sim, cada perfil tem suas próprias métricas; se você administra uma equipe Enterprise, também pode ver o total agregado de todos em Analytics de Equipe.' },
  ],
  problemas: [
    { q: 'Por que meu cartão funcionava antes e agora não funciona mais?', a: 'O mais comum é que o NFC do celular que o aproxima tenha sido desativado, não um problema do cartão: confirme isso primeiro, e se não for, tente o código QR.' },
    { q: 'É normal os emails 2 e 3 de Follow-ups não chegarem?', a: 'Sim, se a detecção de respostas não estiver configurada na sua conta, esses dois emails são omitidos de propósito: o primeiro sempre é enviado.' },
    { q: 'Quanto tempo dura o bloqueio por tentativas falhas de login?', a: 'Alguns minutos. Depois desse tempo você pode tentar novamente com a senha correta.' },
  ],
  cuenta: [
    { q: 'Posso trocar de plano quando quiser?', a: 'Sim, você pode fazer upgrade ou downgrade em Configurações → Plano a qualquer momento.' },
    { q: 'O que acontece se eu cancelar minha assinatura no meio do mês?', a: 'Você mantém o acesso aos recursos do seu plano até o fim do período já pago.' },
    { q: 'Posso recuperar minha conta depois de excluí-la?', a: 'Não, excluir sua conta apaga seu perfil, seus Leads e seus Analytics de forma permanente: não pode ser desfeito.' },
  ],
  developers: [
    { q: 'A API pública tem um custo separado?', a: 'Não, ela está incluída no plano Enterprise.' },
    { q: 'Posso testar a API antes de usá-la em produção?', a: 'Sim, gerando uma API Key em modo sandbox (prefixo cnk_test_), que trabalha com dados de teste.' },
    { q: 'O que acontece se uma entrega de webhook falhar?', a: 'Ela fica registrada no histórico de entregas desse webhook, e você pode tentar novamente manualmente quando quiser.' },
  ],
};

export const DOCS_FAQS_I18N = { es: FAQS_ES, en: FAQS_EN, pt: FAQS_PT };

/* Accessors — DocsPage.js calls these with the current language instead of
   importing flat arrays, so switching language just re-derives the view. */
export const getDocsCategories = (lang) => DOCS_CATEGORIES_I18N[lang] || DOCS_CATEGORIES_I18N.es;
export const getDocsArticles = (lang) => DOCS_ARTICLES_I18N[lang] || DOCS_ARTICLES_I18N.es;
export const getDocsFaqs = (lang) => DOCS_FAQS_I18N[lang] || DOCS_FAQS_I18N.es;
export const getDocsPopularSlugs = (lang) => getDocsArticles(lang).filter(a => a.popular).map(a => a.slug);

